/**
 * „Absenden nach Stille: Auto" — Jev prüft, ob der Satz fertig ist.
 * (29.09.2026, Ticket 2763)
 *
 * Martin, 29.09. 12:50 (Sprechtaste): Bei den Stille-Zeiten soll ein Modus
 * „Auto" stehen. Jev bewertet den Satz: Ist er unfertig, wartet das System
 * weiter. Bewährt sich Auto, entfallen später die festen Sekunden.
 *
 * ## Der Ablauf, eine Regel für App und Server
 *
 *   1. Nach AUTO_FRAGE_AB_MS Stille (seit 02.10.2026; vorher erst nach
 *      AUTO_ERSTE_FRIST_MS) fragt die App den Server mit der bisherigen
 *      Mitschrift (POST /api/stille-auto), der fragt Jev „fertig?". Gesendet
 *      wird frühestens ab AUTO_ERSTE_FRIST_MS.
 *   2. Ab AUTO_FERTIG_AB geht die Nachricht hinaus, darunter wird weiter
 *      gelauscht — spricht man weiter, beginnt es beim nächsten Schweigen neu.
 *      Seit 01.10.2026 (Ticket 2867) SINKT diese Schwelle mit der Stille, in
 *      Stufen (`AUTO_STUFEN`, `autoSchwelle`): Jev wird weiter nur EINMAL je
 *      Text gefragt; sein Urteil bleibt, die Stille wächst, nur die nötige
 *      Sicherheit nimmt ab. Spricht man weiter, kommt neuer Text und die
 *      Stille beginnt bei null — die volle Schwelle gilt wieder.
 *   3. Nie länger als AUTO_HOECHSTENS_MS Stille — außer ein offener Satz,
 *      den Jev klar nicht fertig nennt: AUTO_OFFEN_HOECHSTENS_MS (02.10.2026).
 *   4. Scheitert Jev, antwortet er nicht binnen AUTO_ANTWORT_FRIST_MS, oder
 *      gibt es keine Mitschrift: die feste Frist AUTO_RUECKFALL_MS (mit „?“,
 *      „.“, „!“ am Ende und Antwort des Servers: AUTO_SATZ_RUECKFALL_MS).
 *   5. Das Satzzeichen am Ende senkt Schwelle und Frist (`SATZ_STUFEN`,
 *      unten) — nur, wenn der Server die Regel meldet.
 *   6. Seit 05.10.2026: bei geschlossenem Satz setzt ein Laut, aus dem kein
 *      neues Wort wurde, Jevs Uhr nicht zurück (`autoJevStille`, unten) — nur,
 *      wenn der Server es meldet (`textruhe`).
 *   7. Seit 07.10.2026: endet der Text für Jev mit Komma oder Strichpunkt, gilt
 *      die Obergrenze AUTO_KOMMA_HOECHSTENS_MS (`kommaAmEnde`, unten) — nur,
 *      wenn der Server es meldet (`komma`).
 *   8. Seit 12.10.2026 (Feinschliff nach dem Absenden, ZWEI Schalter am Server):
 *      `fein` (STILLE_FEIN, Standard an): bei geschlossenem Satz (? . !) fragt die
 *      App Jev schon ab AUTO_FRAGE_AB_GESCHLOSSEN_MS (die Sendeschwellen bleiben),
 *      und die Karte wertet Jevs Antwort sofort aus, statt auf den nächsten Takt zu
 *      warten. `deckelKurz` (STILLE_DECKEL_KURZ, Standard AUS): ist Jev bei
 *      geschlossenem Satz klar „nicht fertig“ (< 0,5), gilt AUTO_GESCHLOSSEN_HOECHSTENS_MS
 *      (5 s statt 6 s). Der Deckel ist gebaut und getestet, aber nicht scharf.
 *
 *   9. Seit 12.10.2026 (Bahn „fortsetzung-anhaengen“, `docs/fortsetzung-2026-10-12.md`): Tempo-Stufen `STILLE_TEMPO=ruhig|flott|blitz`
 *      (Standard `ruhig` = alle Regeln wie vorher). `flott` und `blitz` fügen für geschlossene Sätze (? . !) ZUSÄTZLICHE Stufen
 *      hinzu (`TEMPO_STUFEN`, reine Daten); es gilt wie bei jeder Satzzeichen-Stufe die niedrigste Schwelle — eine Tempo-Stufe
 *      sendet also in keiner Lage später als `ruhig`. Sie wirken nur, wenn der Server dazu die Fortsetzung meldet (der Abschnitt
 *      „Fortsetzung“ in shared/fortsetzung.ts macht ein Abschneiden billig); `FORTSETZUNG=aus` erzwingt `ruhig`.
 *
 * ⚠️ Jev nimmt NUR Text (docs.typesafe.ai/concepts/state.md, 29.09.2026:
 *    „Jev accepts text only … Images, audio, and video are not supported
 *    (yet)"). Den Tonfall — ob die Stimme am Satzende fällt — beurteilt Auto
 *    also NICHT, nur die Worte.
 * ⚠️ Ohne Live-Mitschrift (nativer Weg, z. B. bei laufender Tagesaufnahme,
 *    Ticket 2607) gibt es nichts zu prüfen — dort gilt die feste Frist.
 */

/** Der gespeicherte Wert für „Auto" in `sendeStilleMs` (0 = aus, sonst ms). */
export const STILLE_AUTO_MS = -1;
/** So lange Stille, bevor eine Nachricht durch Jevs Urteil hinausgehen kann (gefragt wird früher, `AUTO_FRAGE_AB_MS`). */
export const AUTO_ERSTE_FRIST_MS = 1500;
/**
 * Ab dieser Stille wird Jev GEFRAGT (02.10.2026, Jarvis-Tempo) — gesendet wird
 * weiter frühestens ab `AUTO_ERSTE_FRIST_MS`. Jev braucht ~0,26 s; so liegt sein
 * Urteil schon vor, wenn die erste Frist erreicht ist, und ein „fertig“ in
 * Stufe 1 greift rund 0,45 s früher (im Mittel −0,16 s je Aufnahme). An den
 * Sendeschwellen und an `AUTO_HOECHSTENS_MS` ändert das NICHTS: Abschneiden ist
 * schlimmer als Warten.
 */
export const AUTO_FRAGE_AB_MS = 1000;
/**
 * 12.10.2026 (Bahn „stille-feinschliff“): endet der Text mit „.“, „?“ oder „!“ (`satzEnde`, unabhängig
 * von Jevs Urteil), wird Jev schon ab dieser Stille gefragt — nicht erst ab `AUTO_FRAGE_AB_MS`. Auch das
 * ändert NICHTS an den Sendeschwellen (`SATZ_STUFEN`, frühestens 1,2 s): es verschiebt nur den Moment, in
 * dem das Urteil vorliegt, damit es bei der ersten Sendeschwelle schon da ist und nicht erst 0,25–0,4 s
 * danach eintrifft. Ohne Satzzeichen (offener Satz, Komma, Zahl, Bindewort) gilt weiter 1,0 s.
 * ⚠️ Schalter: `STILLE_FEIN=0` am Server. Die App merkt sich die letzte Antwort (`fein`); vor der ersten
 *    Antwort einer Sitzung und bei `fein: false` gilt 1,0 s wie vorher (`autoFrageAbMs`, `AutoLage.fein`).
 */
export const AUTO_FRAGE_AB_GESCHLOSSEN_MS = 800;
/** Die früheste Stille, ab der überhaupt gefragt wird — die Pause zählt ihre Fragen erst ab hier (ptt-modal.ts). */
export const AUTO_FRAGE_FRUEHESTENS_MS = Math.min(AUTO_FRAGE_AB_MS, AUTO_FRAGE_AB_GESCHLOSSEN_MS);

/**
 * Ab welcher Stille wird Jev für ein Satzende dieser Art gefragt? `AUTO_FRAGE_AB_GESCHLOSSEN_MS` nur bei
 * „.“, „?“, „!“ UND wenn der Server den Feinschliff meldet (`fein`); sonst `AUTO_FRAGE_AB_MS` wie vorher.
 */
export function autoFrageAbMs(satz: SatzEnde | null | undefined, fein = false): number {
  return fein && satz && satz !== "keins" ? AUTO_FRAGE_AB_GESCHLOSSEN_MS : AUTO_FRAGE_AB_MS;
}
/**
 * Ab dieser Wahrscheinlichkeit für „fertig" geht die Nachricht hinaus — in
 * Stufe 1, und auf Dauer, wenn die Stufen aus sind (`STILLE_STUFEN_AUS`).
 */
export const AUTO_FERTIG_AB = 0.8;
/** Die Obergrenze: nie länger Stille als das. */
export const AUTO_HOECHSTENS_MS = 6000;
/** Die feste Frist, wenn Jev nichts beiträgt. */
export const AUTO_RUECKFALL_MS = 4000;
/** So lange wartet die App auf die Antwort, dann gilt der Rückfall. */
export const AUTO_ANTWORT_FRIST_MS = 1000;

/*
 * ── Die gestufte Schwelle (01.10.2026, Tickets 2867 und 2778/2854) ─────────
 *
 * Martin, 01.10. ~10:30: *„Sollte da das Jev-System nicht schon schalten,
 * weil es ganz klar ist, dass jetzt schon die Antwort kommen kann?"* Beleg am
 * Journal (30.09. 12:00 bis 01.10. 12:00, Konto 1, 122 Aufnahmen, 140 Pausen;
 * nachgerechnet in der Nachprüfung vom 01.10.): 50 Schluss-Pausen liefen bis
 * zur Obergrenze, 35 gingen mit der Regel früher hinaus, netto rund 100 s
 * gespart (im Mittel ~2,1 s je Pause bis zur Obergrenze). Eine feste
 * Schwelle 0,8 ließ sie 6 s warten.
 *
 * ⚠️ Ehrliche Grenze: Wer in einer Denkpause von 2,5 s oder mehr zu Ende
 *    denkt, kann abgeschnitten werden — 6 von 33 echten Fortsetzungspausen
 *    (Werte 0,73–0,79, alle in Stufe 2; Stufe 3 ohne belegbaren Fall; die
 *    Pausenlänge steht nicht im Journal, nur nach oben begrenzt).
 *
 * ⚠️ 01.10.2026 13:10–13:13 (pre.3558): vier Aufnahmen wurden mitten im Satz
 *    abgeschickt — dreimal in Stufe 2 nach 2,7 s bei Jev 0,75 / 0,76 / 0,76,
 *    einmal in Stufe 3 bei 0,68 nach 3,7 s; vollständige Sätze lagen im selben
 *    Zeitraum bei ≥ 0,81. Jevs Wert 0,70–0,79 trennt „fertig“ und „Denkpause“
 *    nicht. Martins Regel: Abschneiden ist schlimmer als 1 s länger warten.
 *    Darum jetzt Stufe 2 = 0,80 (greift praktisch nie, Stufe 1 hat es schon
 *    getan) und Stufe 3 = 0,70 (erst ab 3,5 s).
 *
 *    Außerdem schaltet `STILLE_STUFEN_AUS` (am Server, server/stille-auto-routen.ts)
 *    auf die feste 0,8 zurück — nach einem Neustart des Dienstes (nur über
 *    scripts/neustart.sh), denn der Server liest die Umgebung nur beim Start.
 */
/** Ab dieser Stille genügt `AUTO_FERTIG_AB_2`. */
export const AUTO_STUFE_2_MS = 2500;
/**
 * Die Schwelle in Stufe 2 (ab `AUTO_STUFE_2_MS` Stille). Seit pre.3558 gleich
 * `AUTO_FERTIG_AB` — die Stufe bleibt als Zeile in der Tabelle (Protokoll,
 * Karte, Geräte-Logik), greift aber praktisch nicht mehr: was ≥ 0,80 hat,
 * ging schon in Stufe 1 hinaus.
 */
export const AUTO_FERTIG_AB_2 = 0.8;
/** Ab dieser Stille genügt `AUTO_FERTIG_AB_3`. */
export const AUTO_STUFE_3_MS = 3500;
/** Die Schwelle in Stufe 3 (ab `AUTO_STUFE_3_MS` Stille). */
export const AUTO_FERTIG_AB_3 = 0.7;

/*
 * ── Das Satzzeichen am Ende (02.10.2026, Jev nachschärfen) ─────────────────
 *
 * Martin, 02.10. ~18:05: *„Bei einem Fragezeichen ist die Wahrscheinlichkeit,
 * dass der Satz zu Ende ist, noch viel höher; nach einem Punkt oder
 * Ausrufezeichen auch hoch … Und wenn der Satz nicht fertig ist, soll er
 * entsprechend lange warten, das kann ruhig ein paar Sekunden dauern."*
 *
 * Die Live-Mitschrift trägt Satzzeichen: Soniox schickt sie als vorläufige
 * Tokens vor dem Satzende-Signal (soniox.com/docs/stt/rt/endpoint-detection:
 * `{"text": "?", "is_final": false}` … `<end>`), und die Seite nimmt bestätigte
 * UND vorläufige Tokens in den Text (live-dictation.ts `ganz`, seit pre.3625
 * mit Soniox' Wortgrenze — vorher klebten die getrimmten Teile). WANN in der
 * Pause das Zeichen kommt, war nicht gemessen — die Zeile `[stille-auto]`
 * trägt seither `satzzeichen=`, die Art am Text der Frage.
 *
 * Gemessen vor dem Bau (Journal 29.09.–02.10., Konto 1; nur Zahlen):
 *   - Pausen ab 1 s, nach denen weitergesprochen wurde (`[pause-weiter]`,
 *     84): Median 1,25 s, 61 % ≥ 1,2 s, 13 % ≥ 2,5 s, 8 % ≥ 3,5 s, längste 5,7 s.
 *     Auf 73 Aufnahmen kamen 45 solche Pausen mit Jev-Frage — Satzzeichen
 *     ALLEIN (ohne Jev) nach 1,0–1,2 s hätte bis zu 78–100 % davon
 *     abgeschnitten. Darum entscheidet weiter Jev, das Zeichen senkt nur
 *     seine Schwelle und die Frist.
 *   - Jev über die Fortsetzungspausen (326 Fragen): ≥ 0,80 5 %, ≥ 0,60 20 %,
 *     ≥ 0,50 27 %; über die Schlusspausen (350): ≥ 0,80 47 %.
 *   - 62 von 190 Auto-Enden liefen bis zur Obergrenze 6 s; bei 45 davon endete
 *     der gespeicherte Text mit „.“ oder „?“.
 *
 * ⚠️ Der Punkt allein ist kein Satzende im Diktat: Martin hält nach einem
 *    Satz oft inne und spricht weiter. Darum für „.“/„!“ strengere Werte als
 *    für „?“. Ohne Zeichen (auch „,“, „…“, „:“, eine Zahl mit Punkt, „und“
 *    vor dem Zeichen) gilt die Regel von vorher.
 * ⚠️ Schalter: `STILLE_SATZZEICHEN=0` am Server (server/stille-auto-routen.ts,
 *    nach einem Neustart über scripts/neustart.sh). Die App folgt der Antwort
 *    (`satzzeichen: true`); fehlt sie, gilt alles wie vorher.
 */

/** Die Art des Satzendes — nur die Art, nie ein Wort Text. */
export type SatzEnde = "?" | "." | "!" | "keins";

/**
 * Bindewörter, nach denen ein Satz offen ist, auch wenn die Erkennung schon
 * ein Zeichen dahinter setzte („… und.“). Klein geschrieben.
 *
 * ⚠️ Bewusst NICHT dabei, weil sie im Deutschen Sätze beenden: „oder?“ (Frage
 *    am Ende), „denn“/„aber“ (Abtönung: „Was ist denn?“, „Das stimmt aber!“),
 *    „das“/„der“ („Passt dir das?“), „damit“ („Was meinst du damit?“) und
 *    abgetrennte Vorsilben wie „mit“, „an“, „zu“, „auf“ („Kommst du mit?“).
 */
export const OFFENE_WOERTER: ReadonlySet<string> = new Set([
  "und", "weil", "dass", "daß", "sondern", "wenn", "als", "ob", "bzw", "sowie",
  "obwohl", "nachdem", "bevor", "sodass", "während",
]);

/**
 * Ein Apostroph ZWISCHEN zwei Buchstaben gehört zum Wort: „geht's“, „gibt’s“,
 * „schwör's“ (gerade ', typografisch ’, Modifikator ʼ). (06.10.2026)
 *
 * ⚠️ Vorher zerlegte `satzEnde` „geht's.“ in „geht“ + „s“; der eine Buchstabe
 *    vor dem Punkt galt als Abkürzung („z. B.“), der Satz als OFFEN. Beleg
 *    06.10. 10:08 (Konto 1): Schluss „… los geht's.“, Jev 0,48 →
 *    `satzzeichen=keins` in `[stille-auto]` und `[ende-token]`; offen und unter
 *    AUTO_OFFEN_FERTIG_UNTER hob das die Obergrenze auf 9 s statt 6 s
 *    (`autoHoechstensMs`). Martin drückte nach 6,7 s Stille selbst. Im Journal
 *    04.–06.10. (169 Fragen mit Text) zweimal: „schwör's.“ (Jev 0,81, ging
 *    trotzdem gleich hinaus) und dieser Fall.
 *    Die Regel selbst bleibt: Jevs Urteil entscheidet, das Zeichen senkt nur
 *    Schwelle und Frist (`SATZ_STUFEN`). Bei Jev 0,48 nach „.“ geht es jetzt
 *    nach 6 s hinaus — nicht sofort.
 */
const APOSTROPH_IM_WORT = /(?<=\p{L})['’ʼ](?=\p{L})/gu;

/**
 * Welches Satzzeichen steht am Ende? — EINE Funktion für App und Server.
 * Schließende Anführungszeichen und Klammern zählen nicht. „…“/„...“, eine
 * Zahl mit Punkt („am 3.“), ein einzelner Buchstabe mit Punkt („z. B.“) und
 * ein Bindewort vor dem Zeichen (`OFFENE_WOERTER`) gelten als `keins` (offen).
 * Ein Apostroph im Wort trennt nicht (`APOSTROPH_IM_WORT`).
 */
export function satzEnde(text: string): SatzEnde {
  const s = ohneSchliessende(text);
  if (!s || /(\.\.|…)$/u.test(s)) return "keins";
  const zeichen = s.at(-1);
  if (zeichen !== "?" && zeichen !== "." && zeichen !== "!") return "keins";
  if (zeichen === "." && /\d\.$/u.test(s)) return "keins";
  const wort = letztesWort(s.replace(/[?!.]+$/u, ""));
  if (wort.length <= 1 || OFFENE_WOERTER.has(wort)) return "keins";
  return zeichen;
}

/** Der Text ohne schließende Anführungszeichen und Klammern am Ende — für `satzEnde` und `kommaAmEnde`. */
export function ohneSchliessende(text: string): string {
  return text.trim().replace(/["'“”„»«)\]]+$/u, "").trimEnd();
}

/** Das letzte Wort, klein geschrieben — ein Apostroph im Wort trennt nicht (`APOSTROPH_IM_WORT`). */
export function letztesWort(s: string): string {
  return s.replace(APOSTROPH_IM_WORT, "").split(/[^\p{L}\p{N}]+/u).filter(Boolean).at(-1)?.toLowerCase() ?? "";
}

/*
 * ── Komma am Ende (07.10.2026, Bahn „Satzende Komma“) ──────────────────────
 *
 * Martin, 07.10. 17:15: *„Warum kann Jev bei so einem Text nicht automatisch
 * gleich wieder feuern?“* Die Live-Mitschrift stand nach 1,2 s Stille auf
 * „… Code,“ — Jev nannte das unfertig (0,32), ein offener Satz unter
 * AUTO_OFFEN_FERTIG_UNTER darf bis AUTO_OFFEN_HOECHSTENS_MS (9 s) warten. Martin
 * tippte nach 8,5 s selbst auf Senden. Ein zweites Urteil kam nicht: `[ende-token]`
 * sagt `jev=0.32` (das Urteil galt beim Senden noch) UND `satzzeichen=.` — die
 * Mitschrift bekam in der Stille ein Endwort mit „.“, das der Fantasiewörter-Filter
 * für Rauschen hielt (shared/rausch-woerter.ts); der Text für Jev blieb derselbe.
 *
 * Gemessen (Journal 04.–07.10., Konto 1, nur Zahlen und das letzte Zeichen):
 *   - 43 von 370 Fragen an Jev auf Text mit „,“ oder „:“ am Ende. Jev trennt dort
 *     nichts: weitergesprochen Median 0,17, fertig gewesen Median 0,21 (höchstens
 *     0,42) — er sieht nur das Komma.
 *   - 36 Mal ging es weiter (Fortsetzungspausen: längste 3,94 s, zwei ab 3,5 s,
 *     keine ab 4,0 s); 11 weitere Pausen mit demselben Urteil lagen unter 1,6 s.
 *   - 5 Mal war der Satz fertig und wartete 8,5–9,3 s (dreimal bis zur Obergrenze,
 *     zweimal drückte Martin selbst); einmal wurde aus dem Komma noch ein „.“ und
 *     Jev neu gefragt (das tut die App schon, sobald sich der Text für Jev ändert).
 *
 * Darum: bei Komma oder Strichpunkt am Ende — nicht nach einem
 * Bindewort („…, dass,“) — gilt die Obergrenze AUTO_KOMMA_HOECHSTENS_MS, gleich
 * wie Jev urteilt. Jevs Urteil sendet weiter früher, wenn er sicher ist; „,“ bleibt
 * für `satzEnde` „keins“ (keine Satzzeichen-Stufen).
 * Nachgerechnet: 0 der 47 Fortsetzungspausen abgeschnitten, die 5 Schlusspausen
 * 3,0–3,8 s schneller (zusammen ~17 s).
 *
 * Der Doppelpunkt gehört NICHT dazu (Prüfung 07.10.): er kündigt eine Fortsetzung an,
 * und von zwei Fällen war einer die längste Fortsetzung überhaupt (3,94 s, Jev 0,09).
 * Nach einem Doppelpunkt gilt darum die alte Obergrenze.
 *
 * ⚠️ Ehrliche Grenze: 47 Pausen sind wenig, und das Textende steht erst ab 04.10. im
 *    Journal (sechs offene Fortsetzungspausen von mindestens 4,5 s, 01.–03.10. und
 *    07.10. 17:17, haben ein unbekanntes Ende). Ohne Komma (Buchstabe, „—“ am Ende)
 *    gab es im selben Zeitraum Fortsetzungen bis 7,8 s — darum gilt die Grenze NUR
 *    bei Komma. Seit 07.10. misst die Anzeige Pausen mit der Sprachgrenze
 *    (mic-visualizer.ts): ein leiser Ansatz vor dem Weitersprechen setzt die Stille
 *    nicht zurück und verlängert sie gemessen um bis zu 0,78 s (07.10. 17:27), im
 *    Browser-Versuch um 1,1 s. 5,5 s lassen 1,56 s Luft über 3,94 s; 4,5 s ließen
 *    0,56 s und wären daran gescheitert.
 * ⚠️ Schalter: `STILLE_KOMMA=0` am Server (server/stille-auto-routen.ts, nach einem
 *    Neustart über scripts/neustart.sh). Die App folgt der Antwort (`komma: true`);
 *    fehlt sie, gilt alles wie vorher.
 */

/** Die Obergrenze, wenn der Text für Jev mit Komma oder Strichpunkt endet (`kommaAmEnde`). */
export const AUTO_KOMMA_HOECHSTENS_MS = 5500;

/**
 * „Mittleres Urteil“ (08.10.2026, Bahn „stille-schneller“). Martin: *„Warum dauert das
 * Absenden so ewig?“* Journal 07:30–08:30: bei Jev 0,48 und 0,61 (ohne Satzzeichen) wartete
 * die Aufnahme bis AUTO_HOECHSTENS_MS (6 s, `entscheid=hoechstens`), bei 0,80/0,86 nur 1,5–1,7 s.
 * Darum: hat Jev für genau diesen Text `fertig` ab AUTO_OFFEN_FERTIG_UNTER (0,5) und unter
 * AUTO_FERTIG_AB (0,8), gilt statt 6 s `AUTO_MITTEL_HOECHSTENS_MS` — NIE bei klarem „nicht
 * fertig“ (< 0,5, bleibt 9 s) und nie bei Komma (eigene Grenze). Früher als vorher sendet das
 * nur, nie später; Jevs Stufen und Satzzeichen-Stufen senden weiter früher, wo sie es tun.
 *
 * Gemessen (05.–08.10., `[pause-weiter]` gepaart mit dem letzten Urteil davor, 31 Fortsetzungs-
 * pausen bei Jev 0,5–0,8): ohne Satzzeichen 7, längste 2,27 s; nach „?“ 4, längste 2,32 s;
 * nach „.“/„!“ 20, längste 3,88 s (zwei ab 3,0 s: 3,05 und 3,88). Darum 3,0 s für „keins“ und
 * „?“ (0,7 s Luft über 2,32 s; die Sprachgrenze der Anzeige verlängert gemessen bis ~0,8 s),
 * aber 4,5 s nach „.“/„!“ (`AUTO_MITTEL_PUNKT_HOECHSTENS_MS`): 3,0 s hätte dort 2 von 20
 * Fortsetzungen abgeschnitten (10 %). ⚠️ Ehrliche Grenze: 31 Pausen sind wenig; die Paarung
 * nimmt das letzte Urteil in 15 s davor (kann ein früherer Text sein).
 * ⚠️ Schalter: `STILLE_MITTEL=0` am Server; die App folgt der Antwort (`mittel: true`).
 */
export const AUTO_MITTEL_HOECHSTENS_MS = 3000;
/** Wie `AUTO_MITTEL_HOECHSTENS_MS`, wenn der Text mit „.“ oder „!“ endet (längste Fortsetzung dort 3,88 s). */
export const AUTO_MITTEL_PUNKT_HOECHSTENS_MS = 4500;

/**
 * Geschlossener Satz bei klarem „nicht fertig“ (12.10.2026, Bahn „stille-feinschliff“). Martin: *„Warum
 * dauert das nach dem Absenden so lange?“* Journal 12.10. 05:46–06:21 (Konto 1, 12 Aufnahmen): 3 Züge
 * liefen bis zur Obergrenze 6,0 s — der Text endete auf „.“, „?“ oder „!“, Jev gab aber unter 0,5 (er
 * sieht nur Worte, nicht den fallenden Ton). In dieser Klasse (Zeichen, Jev < 0,5) lag keine der 12
 * beobachteten Fortsetzungen zwischen 4,5 und 6 s (längste Pause 2,68 s). Darum 5,0 s statt 6,0 s: 2,3 s
 * Luft über der längsten Pause, die Sprachgrenze der Anzeige verlängert gemessen bis ~0,8 s. Bewusst
 * vorsichtig (nicht 4,5 s): Abschneiden mitten im Satz ist schlimmer als eine Sekunde Warten.
 *
 * Gilt NUR bei Zeichen am Ende (`satz` „.“ „?“ „!“) und Jev < AUTO_OFFEN_FERTIG_UNTER. Offener Satz (9 s),
 * Komma (5,5 s), die Stufen und die Satzzeichen-Stufen bleiben, wie sie sind — das hier kürzt nur die
 * Obergrenze dieser einen Klasse.
 *
 * ⚠️ VORERST NICHT SCHARF (Entscheidung 12.10.2026, Abwägung „lieber 1 s warten als abschneiden“): die
 *    Beleglage sind 12 Aufnahmen aus 35 Minuten, und die Lage ist genau die, in der Jev „nicht fertig“ sagt.
 *    Der Deckel ist gebaut und getestet, gilt aber nur mit `deckelKurz: true` vom Server — Schalter
 *    `STILLE_DECKEL_KURZ=1` (Standard AUS, also 6,0 s wie bisher; erst nach einem Neustart). Ob er später
 *    sinnvoll ist, zeigen die Messfühler: `[ptt-ende] senden … stillMs= jev= satz= autoRestMs=` (Knopf mitten
 *    im Fenster 5–6 s?), `[ende-token] entscheid=hoechstens` mit `jev=` unter 0,50 und `satzzeichen=` . ? !,
 *    `[pause-weiter]` (Fortsetzungen nach langer Pause).
 */
export const AUTO_GESCHLOSSEN_HOECHSTENS_MS = 5000;

/**
 * Frage bei unsicherem Urteil (16.10.2026, Bahn „pause-kuerzen“). Martin, 16.10. 11:29: *„Bitte kürze die Pause
 * etwas … es funktioniert jetzt ganz gut, wir können es zur Not wieder verlängern.“* Journal 04.–16.10. (Konto 1,
 * `[ende-token]`): 20 Schlusspausen mit „.“ „?“ „!“ und Jev 0,30–0,49 liefen bis zur Obergrenze (6,0–6,2 s), 8 davon
 * nach „?“ — darunter die drei `wortruhe`-Fälle vom 16.10. (6,2 s).
 *
 * Gemessen (dieselben Tage, `[pause-weiter]` gepaart mit dem letzten Urteil davor in derselben Aufnahme):
 *   - nach „?“ bei Jev 0,30–0,49: 4 Fortsetzungen, längste 2,50 s (Pegel) bzw. 1,93 s (Ruhe des Textes);
 *     nach „?“ überhaupt (Jev ab 0,3): 38 Fortsetzungen, keine ab 3,0 s Stille am Pegel.
 *   - nach „.“/„!“ bei Jev 0,30–0,49: 29 Fortsetzungen, 5 davon 4,07–5,89 s Stille am Pegel — dort hätte eine
 *     Grenze von 4 s jede sechste Fortsetzung abgeschnitten. Darum NUR „?“; „.“/„!“ bleiben bei 6 s.
 * 4,0 s lassen 1,5 s Luft über der längsten Fortsetzung (die Sprachgrenze der Anzeige verlängert gemessen bis ~0,8 s)
 * und liegen 1 s über der Grenze, die nach „?“ schon für Jev 0,5–0,79 gilt (`AUTO_MITTEL_HOECHSTENS_MS`, 3,0 s).
 * Unter AUTO_FRAGE_KURZ_AB (Jev sagt klar „offen“) und ohne Satzzeichen, bei Komma oder Doppelpunkt ändert sich nichts.
 * ⚠️ Ehrliche Grenze: 4 Fortsetzungen in genau dieser Klasse sind wenig; gestützt wird es von den 38 nach „?“ überhaupt.
 * ⚠️ Schalter: `STILLE_FRAGE_KURZ=0` am Server (Standard an; server/stille-auto-routen.ts, nach einem Neustart über
 *    scripts/neustart.sh). Die App folgt der Antwort (`frageKurz: true`); fehlt sie, gilt alles wie vorher.
 *    Im Journal: `[ende-token] entscheid=geschlossen` mit `satzzeichen=?` (wie der kurze Deckel, der sonst aus ist).
 */
export const AUTO_FRAGE_HOECHSTENS_MS = 4000;
/** Ab diesem Urteil gilt `AUTO_FRAGE_HOECHSTENS_MS` (darunter: Jev nennt den Satz klar offen — 6 s wie vorher). */
export const AUTO_FRAGE_KURZ_AB = 0.3;

/**
 * Endet der Text mit „,“ oder „;“ — eine Pause zwischen zwei Teilen, kein
 * offenes Wort? („:“ nicht: es kündigt eine Fortsetzung an, siehe oben.) Schließende Anführungszeichen und Klammern zählen nicht; nach
 * einem Bindewort (`OFFENE_WOERTER`, „…, dass,“) ist der Satz offen: `false`.
 */
export function kommaAmEnde(text: string): boolean {
  const s = ohneSchliessende(text);
  if (!/[,;]$/u.test(s)) return false;
  const wort = letztesWort(s.replace(/[,;]+$/u, ""));
  return wort !== "" && !OFFENE_WOERTER.has(wort);
}

/**
 * Unterscheiden sich zwei Fassungen NUR im Satzzeichen am Ende („… Code,“ → „… Code.“)?
 * Für die Zeile `[stille-auto]` (`erneut=`): so oft fragt die App neu, nur weil die
 * Erkennung das Satzende nachträgt.
 */
export function nurSatzendeNeu(vorher: string, jetzt: string): boolean {
  const ohne = (t: string): string => ohneSchliessende(t).replace(/[\s,;:.?!…]+$/u, "");
  return vorher.trim() !== jetzt.trim() && ohne(vorher) !== "" && ohne(vorher) === ohne(jetzt);
}

/** Mit der Frage an den Server (07.10.2026): die wievielte in dieser Pause, und ob nur das Satzzeichen am Ende neu ist (`nurSatzendeNeu`). */
export interface AutoFolge {
  frage: number;
  erneut: boolean;
}

/** Eine Stufe der Satzzeichen-Regel: ab dieser Stille genügt diese Wahrscheinlichkeit. */
interface SatzStufe {
  abMs: number;
  fertigAb: number;
}

/**
 * Die Stufen je Satzzeichen — sie gelten ZUSÄTZLICH zu `AUTO_STUFEN`: was dort
 * früher sendet, sendet weiter früher; die Satzzeichen machen nur schneller.
 *
 *   „?“      ab 1,2 s bei ≥ 0,80, ab 2,5 s bei ≥ 0,60, ab 3,5 s bei ≥ 0,50
 *   „.“/„!“  ab 1,2 s bei ≥ 0,85, ab 3,0 s bei ≥ 0,65, ab 4,0 s bei ≥ 0,55
 *
 * Nachgerechnet an 45 Fortsetzungs- und 62 Schlusspausen (02.10. ab 13:32,
 * gepaart mit `[pause-weiter]`), als trüge JEDE Fortsetzungspause das Zeichen
 * (Obergrenze — wahr ist es seltener): je Zeichen 2 von 45 zusätzlich
 * abgeschnitten (4 %; 2,7 % der Aufnahmen); nach „?“ wären 12 von 13
 * Schlusspausen schneller gewesen, nach „.“/„!“ 35 von 48.
 * Geschätzt über die breiteren Zahlen (Jev-Verteilung × Pausenlänge, als
 * unabhängig gerechnet): je Fortsetzungspause 0,8–2,6 % je Stufe.
 * Die 1,2 s sind die Untergrenze: Jev wird ab 1,0 s gefragt und braucht
 * 0,26–0,4 s, vorher liegt kein Urteil vor.
 */
export const SATZ_STUFEN: Readonly<Record<Exclude<SatzEnde, "keins">, readonly SatzStufe[]>> = {
  "?": [
    { abMs: 1200, fertigAb: 0.8 },
    { abMs: 2500, fertigAb: 0.6 },
    { abMs: 3500, fertigAb: 0.5 },
  ],
  ".": [
    { abMs: 1200, fertigAb: 0.85 },
    { abMs: 3000, fertigAb: 0.65 },
    { abMs: 4000, fertigAb: 0.55 },
  ],
  "!": [
    { abMs: 1200, fertigAb: 0.85 },
    { abMs: 3000, fertigAb: 0.65 },
    { abMs: 4000, fertigAb: 0.55 },
  ],
};

/*
 * ── Tempo-Stufen (12.10.2026, Bahn „fortsetzung-anhaengen“) ───────────────
 *
 * Martin: *„Warum dauert das Absenden nach einem fertigen Satz ewig?“* Die Messung (Analyse 12.10., Konto 1, 02.–12.10.) trennt
 * einen fertigen Satz von einer Denkpause über Text und Jev nicht sicher: Weitersprech-Rate nach einer Pause von mindestens t,
 * nach Jev-Band (≥ 0,85 / 0,70–0,85 / 0,55–0,70 / < 0,55):
 *   t = 1,0 s  5,3 % / 8,6 % / 27 % / 41 %      t = 2,0 s  – / 4,3 % / 11 % / 21 %
 *   t = 1,5 s  4,5 % / 6,4 % / 16 % / 28 %      t = 3,0 s  – / 0 % / 6,5 % / 14 %
 * „?“ ist sicherer: 1,7 % bei 1,0 s (Jev ≥ 0,70). Darum erst die Fortsetzung (ein Abschneiden wird erkannt und angehängt), dann
 * diese Stufen — und nur auf Wunsch (Server-Schalter `STILLE_TEMPO`, Standard `ruhig`).
 *
 * Reine Daten. Sie kommen ZUSÄTZLICH zu `SATZ_STUFEN` (`satzStufen`): was dort früher sendet, sendet weiter früher.
 *   flott  „?“     Jev ≥ 0,85 ab 1,2 s · ≥ 0,70 ab 1,5 s · ≥ 0,55 ab 4,0 s
 *          „.“ „!“ Jev ≥ 0,85 ab 1,2 s · ≥ 0,80 ab 1,6 s · ≥ 0,70 ab 2,5 s · ≥ 0,55 ab 4,0 s
 *   blitz  „. ? !“ Jev ≥ 0,85 ab 1,0 s · ≥ 0,70 ab 1,5 s · sonst (jedes bekannte Urteil, ab Jev 0) ab 2,0 s — nie unter 1,0 s
 * (Der Teil unter 0,55 bei `flott` — 6,0 s, mit `STILLE_DECKEL_KURZ` 5,0 s — bleibt, wie er ist: dort ändert `flott` nichts.)
 * ⚠️ `blitz` heißt: Jev wird ab 0,8 s gefragt und braucht ~0,25 s; gesendet wird also frühestens bei rund 1,05 s.
 * ⚠️ „sonst ab 2,0 s“ ist `fertigAb: 0` (ersetzt die frühere Stufe 0,55/2,0 s, die damit überflüssig wurde): auch ein klares „nicht fertig“ (Jev 0,0–0,54) sendet bei geschlossenem Satz nach 2,0 s statt 6,0 s.
 *    Das ist Martins Wunsch (blitz), die Weitersprech-Rate dort liegt bei 3,0 s bei 14 % (bei 2,0 s höher: 21 %) — darum nur auf ausdrücklichen Schalter.
 */
export type StilleTempo = "ruhig" | "flott" | "blitz";

/** Die gültigen Namen — der Schalter des Servers und die Antwort an die App nehmen nur diese. */
export const STILLE_TEMPI: readonly StilleTempo[] = ["ruhig", "flott", "blitz"];

export const TEMPO_STUFEN: Readonly<
  Record<Exclude<StilleTempo, "ruhig">, Readonly<Record<Exclude<SatzEnde, "keins">, readonly SatzStufe[]>>>
> = {
  flott: {
    "?": [
      { abMs: 1200, fertigAb: 0.85 },
      { abMs: 1500, fertigAb: 0.7 },
      { abMs: 4000, fertigAb: 0.55 },
    ],
    ".": [
      { abMs: 1200, fertigAb: 0.85 },
      { abMs: 1600, fertigAb: 0.8 },
      { abMs: 2500, fertigAb: 0.7 },
      { abMs: 4000, fertigAb: 0.55 },
    ],
    "!": [
      { abMs: 1200, fertigAb: 0.85 },
      { abMs: 1600, fertigAb: 0.8 },
      { abMs: 2500, fertigAb: 0.7 },
      { abMs: 4000, fertigAb: 0.55 },
    ],
  },
  blitz: {
    "?": [
      { abMs: 1000, fertigAb: 0.85 },
      { abMs: 1500, fertigAb: 0.7 },
      { abMs: 2000, fertigAb: 0 },
    ],
    ".": [
      { abMs: 1000, fertigAb: 0.85 },
      { abMs: 1500, fertigAb: 0.7 },
      { abMs: 2000, fertigAb: 0 },
    ],
    "!": [
      { abMs: 1000, fertigAb: 0.85 },
      { abMs: 1500, fertigAb: 0.7 },
      { abMs: 2000, fertigAb: 0 },
    ],
  },
};

/** Die frühestmögliche Sendestille aller Tempo-Stufen: darunter sendet nichts (die Untergrenze von `blitz`). */
export const TEMPO_UNTERGRENZE_MS = 1000;

/** Nur ein ausdrückliches „flott“ oder „blitz“ zählt; alles andere (fehlt, Unsinn, falscher Typ) ist `ruhig` — im Zweifel das alte Verhalten. */
export function tempoLesen(roh: unknown): StilleTempo {
  return roh === "flott" || roh === "blitz" ? roh : "ruhig";
}

/**
 * Die feste Frist ohne Jev-Urteil, wenn der Text mit „?“, „.“ oder „!“ endet
 * (statt `AUTO_RUECKFALL_MS`, 4 s). Fortsetzungspausen ≥ 3,0 s: 10 %, ≥ 4,0 s:
 * 8 % — und der Rückfall ist selten (8 von 190 Auto-Enden).
 */
export const AUTO_SATZ_RUECKFALL_MS = 3000;

/*
 * ── Kurze Zusage (15.10.2026, Bahn „stille-zusage“) ────────────────────────
 *
 * Martin, 15.10. (wütend): Er sagte „Okay, und weiter“ — das Mikro war nach Vesperas Frage von selbst offen —
 * und rund 29 s ging nichts hinaus. Journal: `[stille-auto] fertig=0.32 entscheid=warten satzzeichen=keins`,
 * Endwörter „Okay, und weiter:“, gesendet erst über das Fenster (`autoRestMs=9000`). Die Mitschrift endete mit
 * „:“ statt „.“; ohne Zeichen und bei Jev < 0,5 gilt der Satz als klar offen (AUTO_OFFEN_HOECHSTENS_MS, 9 s).
 * Jev sieht in drei Wörtern zu wenig, um „fertig“ zu sagen — gerade die kürzesten, eindeutigsten Antworten
 * warteten am längsten.
 *
 * Darum: besteht der GANZE Text aus höchstens ZUSAGE_HOECHSTENS_WOERTER Wörtern dieser festen Tabelle (Bestätigung
 * oder Weiter-Befehl) und endet nicht auf einem Bindeglied, geht er nach AUTO_ZUSAGE_AB_MS Stille hinaus — gleich,
 * was Jev sagt. Ein einziges anderes Wort („Okay, und dann erkläre mir bitte“, „Ja, aber“) und alles bleibt wie
 * vorher. Die Fortsetzung (shared/fortsetzung.ts) hängt an, falls doch noch etwas kommt.
 *
 * Das Zeichen am Ende (`zusageEndeOffen`), entschieden an dem, was gemessen ist:
 *   - „:“ blockiert die Zusage NICHT. Gemessen sind nur zwei Doppelpunkt-Pausen (Komma-Abschnitt oben: eine davon
 *     die längste Fortsetzung, 3,94 s bei Jev 0,09) — zu wenig, um einen Doppelpunkt hinter einem vollständigen
 *     Befehl als Ankündigung zu lesen; der Fall vom 15.10. zeigt die Erkennung, die ihn hinter „weiter“ setzt.
 *     Für alle anderen Sätze bleibt „:“, wie es war (`satzEnde` „keins“, alte Obergrenze) — dort ändert sich nichts.
 *   - „,“ „;“ blockieren: eine Pause zwischen zwei Teilen (Komma-Abschnitt: 36 von 43 Komma-Pausen gingen weiter).
 *   - „—“ „–“ „-“ blockieren: ohne Komma (Buchstabe, „—“) gab es Fortsetzungen bis 7,8 s (ebenda).
 *   - „…“/„...“ blockieren: Auslassung ist Abbrechen mitten im Gedanken; `satzEnde` nennt sie schon offen, gemessen
 *     ist dazu nichts — also im Zweifel warten.
 *   - „.“ „!“ „?“ und kein Zeichen: zählen nicht dagegen.
 * Wortgrenzen: `\b` kennt keine Umlaute — zerlegt wird an `\p{L}`/`\p{N}` wie in `letztesWort`.
 *
 * ⚠️ Ehrliche Grenze: ein Vorab-Wort aus der Tabelle, nach dem jemand 1,2 s nachdenkt („Okay … [Pause] … und dann
 *    erkläre mir“), geht nach 1,2 s hinaus. Das fängt die Fortsetzung auf (Anhängen binnen FORTSETZUNG_FENSTER_MS) —
 *    ein vollständiger Befehl, auf den 29 s nichts passiert, ist schlimmer.
 * ⚠️ Schalter: `STILLE_ZUSAGE=0` am Server (server/stille-auto-routen.ts, nach einem Neustart über
 *    scripts/neustart.sh). Die App folgt der Antwort (`zusage: true`, sie merkt sich die letzte); fehlt sie, gilt
 *    alles wie vorher.
 */

/** Ab dieser Stille geht eine kurze Zusage hinaus (die Untergrenze der Satzzeichen-Stufen, `SATZ_STUFEN`). */
export const AUTO_ZUSAGE_AB_MS = 1200;
/**
 * 17.10.2026 (Fall 16:04): war eine FRAGE offen (das Mikro ging nach ihr von selbst auf), geht eine kurze Zusage schon nach so viel
 * Stille hinaus — „sofort“, nur eine Atempause lang (das Ja kam sonst erst nach 19 s an). Ein „Ja, aber …“ danach fängt die
 * Fortsetzung (shared/fortsetzung.ts) auf; wer im Text schon weitersprach, ist keine Zusage mehr (`kurzeZusage`).
 */
export const AUTO_ZUSAGE_FRAGE_AB_MS = 400;
/** Höchstens so viele Wörter hat eine kurze Zusage („okay und mach weiter“). */
export const ZUSAGE_HOECHSTENS_WOERTER = 4;

/** Wörter, die allein eine vollständige Antwort sind — Bestätigung oder Weiter-Befehl. Klein geschrieben. */
const ZUSAGE_WOERTER: ReadonlySet<string> = new Set([
  "okay", "ok", "okey", "oke", "ja", "jo", "jep", "jawohl", "genau", "gut", "passt", "super", "perfekt",
  "klar", "richtig", "stimmt", "einverstanden", "natürlich", "gerne", "gern", "bitte", "danke",
  "nein", "nee",
  "mach", "machs", "mache", "weiter", "fortfahren", "los", "gehts",
]);
/**
 * Bindeglieder: erlaubt ZWISCHEN Zusage-Wörtern („okay und weiter“, „ja dann los“, „mach mal weiter“, „alles
 * klar“), aber nie am Ende — „ja und“, „okay dann“, „okay alles“ sind offen.
 */
const ZUSAGE_BINDEGLIEDER: ReadonlySet<string> = new Set(["und", "dann", "jetzt", "mal", "alles"]);

/** Endet der Text auf einem Zeichen, nach dem gemessen oder im Zweifel noch etwas kommt? (Begründung oben.) */
function zusageEndeOffen(s: string): boolean {
  return /(\.\.|…|[,;—–-])$/u.test(s);
}

/**
 * Ist der ganze Text eine kurze Zusage? (`ZUSAGE_WOERTER`, höchstens `ZUSAGE_HOECHSTENS_WOERTER` Wörter, nicht auf
 * einem Bindeglied endend, kein offenes Zeichen am Ende — ein „:“ dort zählt nicht dagegen.)
 */
export function kurzeZusage(text: string): boolean {
  const s = ohneSchliessende(text);
  if (!s || zusageEndeOffen(s)) return false;
  const woerter = s.replace(APOSTROPH_IM_WORT, "").toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (!woerter.length || woerter.length > ZUSAGE_HOECHSTENS_WOERTER) return false;
  if (!woerter.every((w) => ZUSAGE_WOERTER.has(w) || ZUSAGE_BINDEGLIEDER.has(w))) return false;
  return !ZUSAGE_BINDEGLIEDER.has(woerter.at(-1) ?? "");
}

/**
 * „Ja“ oder „Nein“ für die Rückmeldung „Ja gesendet“ — nur, wenn das ERSTE Wort ein Ja oder ein Nein ist (ja/jo/jep/jawohl,
 * nein/nee). Bei „danke“, „bitte“, „okay“, „weiter“ … `null`: die Karte sagt dann neutral „Gesendet“ (Sicherheitsprüfung S1).
 */
export function zusageAntwort(text: string): "Ja" | "Nein" | null {
  const erstes = text.toLowerCase().split(/[^\p{L}\p{N}]+/u).find(Boolean) ?? "";
  const woerter = text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  const nein = (w: string): boolean => w === "nein" || w === "nee";
  const ja = (w: string): boolean => ["ja", "jo", "jep", "jawohl"].includes(w);
  // „ja nein“ (Ja- und Nein-Wort zusammen) ist keins von beiden — neutral.
  if (woerter.some(ja) && woerter.some(nein)) return null;
  if (ja(erstes)) return "Ja";
  return nein(erstes) ? "Nein" : null;
}

/*
 * ── Offenes Ende: ohne Satzzeichen strenger (16.10.2026, Bahn „jev-offenes-ende“) ──
 *
 * Martin, 16.10. (Freigabe: „das Jev nachbearbeiten“): sein Satz ging weg, obwohl er noch weitersprach. Beleg Journal
 * 16.10. 12:35 (Konto 1, nur Zahlen): `[stille-auto] fertig=0.87 stillMs=1134 satzzeichen=keins`, gesendet bei
 * `[ende-token] entscheid=urteil stufe=1 stillMs=1584` — mitten im Gedanken, die Mitschrift ohne Zeichen am Ende.
 * Jevs 0,80–0,91 trennt dort Satzende und Denkpause nicht (Stufen-Abschnitt oben: Fortsetzungen bis 0,79 in Stufe 2,
 * Weitersprech-Rate bei Jev 0,70–0,85 nach 1,5 s noch 6,4 %, Tempo-Abschnitt).
 *
 * Martins Regel, so gebaut:
 *   - Endet die Mitschrift OHNE Satzzeichen („.“ „!“ „?“ „…“), sendet Jevs Urteil vor OFFEN_STILLE_AB_MS (2,5 s) nur ab
 *     OFFEN_FERTIG_AB (0,92); ab 2,5 s gelten die Stufen wie vorher (Stufe 2: 0,80, Stufe 3: 0,70).
 *   - Endet sie auf einem Füllwort (`ANSCHLUSS_WOERTER`: „ähm“, „äh“, „also“, „und“, „aber“, „weil“ …) oder auf „,“ „;“ „:“
 *     „-“ „–“ „—“, sendet Jevs Urteil vor 2,5 s GAR NICHT; ab 2,5 s braucht es weiter Jevs Freigabe nach den Stufen.
 *   - Mit Satzzeichen am Ende ändert sich nichts (auch nicht nach „?“, `AUTO_FRAGE_HOECHSTENS_MS`).
 * Die Regel macht NUR später, nie früher, und nur das Senden über Jevs Urteil: jede Obergrenze (`autoHoechstensMs`, die
 * kürzeste 3,0 s), der Rückfall ohne Jev, die kurze Zusage und die Wortstille (die selbst erst ab 2,5 s Ruhe zählt)
 * bleiben, wie sie sind — Martin hängt nie länger fest als vorher.
 * Wortgrenzen: `\b` kennt keine Umlaute — zerlegt wird an `\p{L}`/`\p{N}` wie in `letztesWort`.
 *
 * ⚠️ Schalter: `STILLE_OFFEN_STRENG=0` (auch „false“, „nein“, „aus“) am Server (server/stille-auto-routen.ts, Standard an,
 *    nach einem Neustart über scripts/neustart.sh). Die App folgt der Antwort (`offenStreng: true`, nur für genau diesen
 *    Text); fehlt sie, gilt alles wie vorher. Im Journal: `[stille-auto] … offen=offen|anschluss`
 *    (nur ohne Satzzeichen und mit Regel an), `[ende-token] stillMs=` zeigt, wann es hinausging.
 */

/** Ohne Satzzeichen am Ende sendet Jevs Urteil vor `OFFEN_STILLE_AB_MS` erst ab dieser Wahrscheinlichkeit. */
export const OFFEN_FERTIG_AB = 0.92;
/** Ab dieser Stille gelten für ein offenes Ende wieder die Stufen von vorher. */
export const OFFEN_STILLE_AB_MS = 2500;

/** Füllwörter und Bindewörter, nach denen der Gedanke weitergeht. Klein geschrieben; dazu `OFFENE_WOERTER`. */
const ANSCHLUSS_WOERTER: ReadonlySet<string> = new Set([
  ...OFFENE_WOERTER,
  "äh", "ähh", "ähm", "ähhm", "öhm", "ehm", "hm", "hmm", "also", "aber", "oder", "denn", "dann", "sprich", "quasi", "beziehungsweise",
]);

/**
 * Die Art des offenen Endes: `null` — der Text endet mit „.“ „!“ „?“ „…“ (oder ist leer): nichts ändert sich;
 * `anschluss` — Füllwort/Bindewort (`ANSCHLUSS_WOERTER`) oder „,“ „;“ „:“ „-“ „–“ „—“ am Ende; `offen` — sonst ohne Zeichen.
 * Schließende Anführungszeichen und Klammern zählen nicht. Nur die Art, nie ein Wort Text.
 */
export type OffenesEnde = "offen" | "anschluss";

export function offenesEnde(text: string): OffenesEnde | null {
  const s = ohneSchliessende(text);
  if (!s || /[.!?…]$/u.test(s)) return null;
  if (/[,;:\-–—]$/u.test(s)) return "anschluss";
  return ANSCHLUSS_WOERTER.has(letztesWort(s)) ? "anschluss" : "offen";
}

/** Die Schwelle für ein offenes Ende: vor `OFFEN_STILLE_AB_MS` mindestens `OFFEN_FERTIG_AB` bzw. gar nicht (`anschluss`). */
function offenSchwelle(schwelle: number, stillMs: number, ende: OffenesEnde | null | undefined): number {
  if (!ende || !(stillMs < OFFEN_STILLE_AB_MS)) return schwelle;
  return ende === "anschluss" ? Number.POSITIVE_INFINITY : Math.max(schwelle, OFFEN_FERTIG_AB);
}
/**
 * Die Obergrenze, wenn der Satz OFFEN ist (kein Satzzeichen) und Jev ihn klar
 * nicht fertig nennt (`fertig` < AUTO_OFFEN_FERTIG_UNTER). Martin: *„das kann
 * ruhig ein paar Sekunden dauern. Und wenn dann nichts mehr kommt, dann halt
 * entsprechend reagieren.“* Fortsetzungspausen reichten bis 5,7 s; an der
 * alten Grenze 6 s blieb dafür kaum Luft. Alle anderen Fälle behalten
 * `AUTO_HOECHSTENS_MS`.
 */
export const AUTO_OFFEN_HOECHSTENS_MS = 9000;
/** Unter diesem Wert gilt ein offener Satz als „klar nicht fertig“. */
export const AUTO_OFFEN_FERTIG_UNTER = 0.5;

/*
 * ── Kurze Obergrenze (16.10.2026, Bahn „obergrenze-kurz“) ───────────────────
 *
 * Martin, 16.10. 18:28 (Freigabe per Knopf): *„Obergrenze auf 4 s senken, nach ‚ähm‘/Komma auf 6 s. Dann sendet es nach
 * spätestens 4 s, auch wenn Jev unsicher ist.“* Beleg Journal 16.10. (Konto 1, `[ende-token] entscheid=hoechstens`, nur
 * Zahlen): drei Sendungen nach 9,0–9,2 s (`satzzeichen=keins`, Jev 0,08 / 0,15 / 0,37) — das ist `AUTO_OFFEN_HOECHSTENS_MS`
 * (ohne Zeichen und Jev < 0,5); vier weitere nach 6,1 s mit „.“ (Jev 0,20–0,47) — `AUTO_HOECHSTENS_MS`. 09.–16.10.: 30
 * Sendungen über die Obergrenze, 5 davon ohne Zeichen (alle 9,0–9,8 s).
 *
 * Darum gilt, mit Ja des Servers für genau diesen Text, als Obergrenze die KÜRZERE von bisher und
 * `AUTO_OBERGRENZE_KURZ_MS` (4 s) — bzw. `AUTO_OBERGRENZE_ANSCHLUSS_MS` (6 s), wenn der Text auf Füllwort, Bindewort, „,“
 * „;“ „:“ oder Strich endet (`offenesEnde` = `anschluss`, dieselbe Erkennung wie die Regel „offenes Ende“). Nie später als
 * vorher: jede kürzere Grenze (Komma 5,5 s, mittleres Urteil 3,0/4,5 s, „?“ 4,0 s), Jevs Stufen, die Satzzeichen-Stufen,
 * der Rückfall, die kurze Zusage und die Wortstille bleiben, wo sie früher senden. Die Wortstille (die Ruhe des Textes)
 * zählt wie die Stille am Pegel auch gegen diese Grenze — wie gegen jede andere.
 *
 * ⚠️ Ehrliche Grenze: 04.–16.10. kamen von 565 Pausen, nach denen weitergesprochen wurde (`[pause-weiter]`), 18 erst ab 4 s
 *    Stille (3 %), 2 ab 6 s — nach „.“/„!“ bei Jev 0,30–0,49 waren es 5 von 29 (Abschnitt „Frage bei unsicherem Urteil“).
 *    Die schneidet die Grenze jetzt ab; die Fortsetzung (shared/fortsetzung.ts) hängt sie an.
 * ⚠️ Schalter: `STILLE_OBERGRENZE_KURZ=0` (auch „false“, „nein“, „aus“) am Server (server/stille-auto-routen.ts, Standard an,
 *    nach einem Neustart über scripts/neustart.sh) — dann gelten wieder 9 s / 6 s. Die App folgt der Antwort
 *    (`obergrenzeKurz: true`, nur für genau diesen Text); fehlt sie, gilt alles wie vorher. Im Journal:
 *    `[ende-token] entscheid=obergrenze stillMs=` (nur wenn erst diese Grenze sendete, die von vorher noch nicht).
 */
/** Die kurze Obergrenze: so lange Stille, dann geht die Nachricht hinaus, gleich was Jev sagt. */
export const AUTO_OBERGRENZE_KURZ_MS = 4000;
/** Wie `AUTO_OBERGRENZE_KURZ_MS`, wenn der Text auf Füllwort/Komma/Doppelpunkt/Strich endet (`offenesEnde` = `anschluss`). */
export const AUTO_OBERGRENZE_ANSCHLUSS_MS = 6000;

/**
 * Die kurze Obergrenze für diesen Text, nie länger als `bisherMs` (die Grenze nach `autoHoechstensMs`). EINE Rechnung für die
 * App (`autoPruefung`) — der Server rechnet die Grenze nicht, er sagt nur, ob sie gilt.
 */
export function obergrenzeKurzMs(bisherMs: number, text: string): number {
  return Math.min(bisherMs, offenesEnde(text) === "anschluss" ? AUTO_OBERGRENZE_ANSCHLUSS_MS : AUTO_OBERGRENZE_KURZ_MS);
}

/*
 * ── Laut ohne neues Wort (05.10.2026, Bahn „Satzende schneller“) ──────────
 *
 * Martin, 05.10.: *„Außerdem kann Jev nie darauf reagieren, dass der Satz aus
 * ist.“* Beleg 05.10. 08:01: der Satz endete mit „.“, Jev gab 0,68 — die
 * Mitschrift stand beim Abschicken seit 17,3 s still (`textRuhtMs=17318`), der
 * Pegel erst seit 3,1 s. Davor hörte er ~14 s Geräusch mit zwei Pausen von je
 * ~1 s (`[pause-weiter]` 1070/1064), aus dem die Live-Erkennung kein Wort
 * machte (die Dateierkennung am Server machte Fantasiewörter daraus). Jeder Laut
 * stellte die Stille auf null — und Jevs Schwelle zurück an den Anfang.
 *
 * Gemessen (Journal 01.–05.10., Konto 1, 238 Auto-Sendungen mit Live-Mitschrift):
 *   - Normal steht der Text KÜRZER still als der Pegel: das letzte Wort kommt im
 *     Median 625 ms nach dem Beginn der Stille an (Stille − Textruhe beim Senden).
 *   - In 47 von 238 (20 %) stand er LÄNGER still, in 32 um mehr als 2 s: Laut
 *     ohne Wort nach dem letzten Wort. In den 9 Fällen mit Satzzeichen vom
 *     04./05.10. (dort steht das Ende der Mitschrift im Journal) endete die
 *     gespeicherte Nachricht achtmal mit demselben Satz, einmal (05.10. 08:01)
 *     folgten nur Fantasiewörter.
 *
 * Darum: Bei geschlossenem Satz (? . !) misst JEVS SCHWELLE ab TEXTRUHE_AB_MS
 * Stille am Pegel die Ruhe des Textes, wenn er um mindestens TEXTRUHE_VORLAUF_MS
 * länger steht. Obergrenze, Rückfall, Frist und jeder offene Satz bleiben an der
 * Stille des Pegels.
 *
 * Nachgerechnet (02.10. 18:40 bis 05.10. 08:05, 59 echte Fortsetzungspausen und
 * 67 Schlusspausen mit Urteil): 0 Fortsetzungen zusätzlich abgeschnitten, 5
 * Schlusspausen 1,1–1,7 s schneller (3,1–4,2 s → 2,0–2,7 s, zusammen 6,6 s),
 * darunter 05.10. 08:01. Bei einem sauber beendeten Satz OHNE Geräusch danach
 * ändert sich nichts: dort trennt Jevs 0,65–0,79 Satzende und Denkpause nicht
 * (echte Fortsetzungen nach 2,1–2,9 s mit denselben Werten).
 *
 * ⚠️ Ehrliche Grenze: Die ~14 s Geräusch VOR der Stille vom 05.10. verkürzt das
 *    nicht — dort war der Pegel nie 2 s still. Dort liegt der größere Verlust:
 *    in 36 der 238 Sendungen stand der Text 1,2–28,8 s länger als der Pegel
 *    (Median 3,6 s, zusammen 191 s; ein Ausreißer mit 707 s nicht gezählt).
 *    Dafür müsste die Karte auch MITTEN im Geräusch entscheiden; ob Sprechen
 *    so lange ohne Wort bleiben kann, misst erst die Zeile `[ende-token]`
 *    (`ohneWortMaxMs`, ende-token.ts).
 * ⚠️ Schalter: `STILLE_TEXTRUHE=0` am Server (server/stille-auto-routen.ts, nach
 *    einem Neustart über scripts/neustart.sh). Die App folgt der Antwort
 *    (`textruhe: true`); fehlt sie, gilt alles wie vorher.
 */

/**
 * Ab so viel Stille am Pegel zählt ein Laut ohne neues Wort nicht mehr als
 * Sprechen: neue Wörter kamen bis 1,65 s nach Beginn der Stille an (04.10.,
 * 3 Fälle mit Text); was später kommt (Satzzeichen, Wortreste bis 2,9 s), ändert
 * den Text und beendet die Brücke von selbst.
 */
export const TEXTRUHE_AB_MS = 2000;
/**
 * Um mindestens so viel muss der Text schon VOR der Stille gestanden haben: ein
 * kürzerer Abstand ist Nachhall des Pegels oder ein spätes Wort (normal kommt das
 * letzte Wort 0,4–0,8 s NACH dem Beginn der Stille), kein Laut ohne Wort. Von den
 * 47 Sendungen mit längerer Textruhe lagen 10 darunter (84–907 ms), 37 bei 1,2 s
 * und mehr.
 */
export const TEXTRUHE_VORLAUF_MS = 1000;

/*
 * ── Wortstille: der Text ruht, der Pegel nicht (15.10.2026, Bahn „wortstille“) ──
 *
 * Martin, 15.10. (wütend, wiederholt): die Nachricht geht viel zu spät hinaus, obwohl er „gar nichts mehr
 * sagt“. Beleg 15.10. 21:10 (Journal, nur Zahlen): Auto-Mikro nach Frage, Schluss mit „.“, Jev 0,79 — gefragt
 * bei 917 ms Stille, danach KEIN Entscheid mehr (`[ende-token] entscheid=-`). Der Pegel am Satzende lag bei
 * 0,38–0,53, die Grenze bei 0,041 (zehnfach darüber): die Stille am Pegel erreichte nie wieder 1 s, geschweige
 * denn die 3,0 s, ab der Jevs 0,79 nach „.“ genügt. Gesendet hat erst Martins Knopf (`anlass=fenster-senden`,
 * `autoRestMs=2000` bei `stillMs=0`), nach geschätzt 6–9 s ohne Wort (`ohneWortEndeMs=3283`: so viel
 * anhaltender Ton ohne neues Wort nach der letzten Änderung des Textes).
 *
 * Warum nichts griff: JEDE Schwelle, jede Obergrenze und der Rückfall hingen an der Stille des PEGELS. Die
 * Brücke „Laut ohne neues Wort“ (`autoJevStille`) öffnet erst ab TEXTRUHE_AB_MS (2 s) Stille am Pegel, die
 * Sprachgrenze der Anzeige hebt die Grenze höchstens auf 0,083 — beides unter Dauerton nie. Jev wird je Text
 * einmal gefragt; sein Urteil lag vor, nur die Stille, an der es gemessen wird, wuchs nicht.
 *
 * Darum: ruht der Text der Live-Mitschrift seit mindestens WORTRUHE_AB_MS, zählt seine Ruhe als Stille
 * (`autoWortStille`) — für Frage, Stufen, Satzzeichen-Stufen, Obergrenze und Rückfall, wie die Stille am Pegel.
 * Dazu die harte Obergrenze WORTRUHE_GESCHLOSSEN_HOECHSTENS_MS nach dem letzten Wort bei geschlossenem Satz
 * („.“ „?“ „!“) und Jev ab 0,5. Geräusch ohne Wort hält so nie länger als die Obergrenze seiner Lage.
 * Was die Stille am Pegel allein schon sendet, bleibt mit seinem Grund; nur was erst die Ruhe des Textes
 * sendet, heißt `wortruhe` (`[ende-token] entscheid=wortruhe`, `jevStillMs=` die Ruhe des Textes).
 *
 * ⚠️ Warum 2,5 s und nicht weniger: die Mitschrift kommt nach dem Ton. Gemessen ist Ton ohne Wort, nach dem
 *    DOCH noch Text kam (`ohneWortMaxMs`), bis 1,65 s (15.10. und 04.10.); die Sprachgrenze öffnet aus demselben
 *    Grund erst rund 2,7 s nach dem letzten Wort. Darunter sendet die Ruhe des Textes nie — eine Stufe, die an
 *    der Stille des Pegels früher erlaubt wäre (1,2/1,5 s), greift an der Ruhe des Textes erst bei 2,5 s.
 * ⚠️ Nur mit Live-Mitschrift: beim Nachhören (Mitschrift hängt) kommt der Text in Schüben, seine Ruhe sagt
 *    nichts (ptt-modal.ts gibt dort keine `textRuhtMs`). Ein neues Wort ändert den Text — die Ruhe beginnt bei
 *    null, und es gilt wieder allein der Pegel. Ein Abschneiden fängt die Fortsetzung auf (shared/fortsetzung.ts).
 * ⚠️ Schalter: `STILLE_WORTRUHE=0` am Server (server/stille-auto-routen.ts, nach einem Neustart über
 *    scripts/neustart.sh). Die App folgt der Antwort (`wortruhe: true`; vor dem Urteil für genau diesen Text
 *    die letzte Antwort); fehlt sie, SENDET die Ruhe des Textes nichts. Gefragt wird an ihr immer (eine Frage
 *    schickt nichts ab — und erst ihre Antwort sagt einer frisch geladenen Seite, ob die Regel gilt).
 */

/** Ab so langer Ruhe des Textes zählt sie als Stille, auch wenn der Pegel Ton zeigt. */
export const WORTRUHE_AB_MS = 2500;
/** Die harte Obergrenze nach dem letzten Wort: geschlossener Satz („.“ „?“ „!“) und Jev ab 0,5. */
export const WORTRUHE_GESCHLOSSEN_HOECHSTENS_MS = 4000;

/*
 * ── Wortstille bei klar unfertigem Satz unter Ton: später (17.10.2026, Bahn „wortstille-unfertig“) ──
 *
 * Martin, 17.10. (Ja zum Umbau): der Satz ging mitten drin hinaus, der Rest kam als zweite Nachricht. Beleg Journal
 * 17.10. 08:16 (Mac, `via=knopf`, nur Zahlen): `[ptt-ende] senden … anlass=selbst-stille`, `[ende-token]
 * entscheid=wortruhe stufe=3 textRuhtMs=4161 stillMs=0 endpegel=1.00`, Jev `fertig=0.08`, kein Satzzeichen am Ende.
 * Der Pegel zeigte die ganze Zeit Ton — Martin sprach weiter, nur die Live-Mitschrift hing; nach 4,0 s Ruhe des
 * Textes griff an ihr die kurze Obergrenze (`obergrenzeKurzMs`, 4 s ohne Zeichen).
 *
 * Darum: Trifft ALLES zusammen — Urteil für genau diesen Text mit `fertig` < WORTRUHE_UNFERTIG_UNTER, kein
 * Satzzeichen am Ende (`satzEnde` = `keins`) und der Pegel zeigt Ton (Stille am Pegel < WORTRUHE_TON_UNTER_MS) —,
 * zählt die Ruhe des Textes erst nach WORTRUHE_UNFERTIG_AUFSCHUB_MS. Jeder Weg der Wortstille (Stufen, Obergrenze,
 * Rückfall) greift dann genau um diesen Aufschub später, nie mehr: der harte Deckel. Fall 17.10.: 4,0 s → 10,0 s.
 * Wird der Pegel still (≥ WORTRUHE_TON_UNTER_MS), gilt die Wortstille sofort wie vorher; die Stille am Pegel
 * (`autoPruefungPegel`) ändert sich NIE. Ein fertiger oder geschlossener Satz ändert sich nicht (Tempo bleibt).
 *
 * ⚠️ Grenze: Endet ein wirklich fertiger Satz ohne Zeichen, Jev unter 0,2, und hält danach Geräusch ohne Wort an,
 *    wartet er bis zu WORTRUHE_UNFERTIG_AUFSCHUB_MS länger als vorher. Der Pegel allein kennt Sprache und Geräusch
 *    nicht auseinander (`endpegel` steht nicht in der Lage, nur die Stille am Pegel).
 * ⚠️ Schalter: `STILLE_WORTRUHE_UNFERTIG=0` (auch „false“, „nein“, „aus“) am Server (server/stille-auto-routen.ts, Standard an,
 *    nach einem Neustart über scripts/neustart.sh) schaltet NUR den Aufschub ab; die Wortstille selbst bleibt. Die App folgt
 *    dem Urteil für genau diesen Text (`wortruheUnfertig: true`); fehlt es, gilt alles wie vorher. `STILLE_WORTRUHE=0` schaltet
 *    die Wortstille samt Aufschub ab. Im Journal: `regelv=7` (seit 26.10. `regelv=8`, Abschnitt unten).
 */
/** Unter diesem Jev-Wert gilt ein Satz ohne Zeichen als „klar unfertig“ (Fall 17.10.: 0,08). */
export const WORTRUHE_UNFERTIG_UNTER = 0.2;
/**
 * Unter so viel Stille am Pegel zeigt der Pegel noch Sprache/Ton. Gleich `TEXTRUHE_AB_MS`: neue Wörter kamen bis
 * 1,65 s nach Beginn der Stille an — darunter kann die Mitschrift noch hinterherhinken.
 */
export const WORTRUHE_TON_UNTER_MS = TEXTRUHE_AB_MS;
/** Der Deckel: um höchstens so viel sendet die Wortstille bei klar unfertigem Satz unter Ton später als vorher. */
export const WORTRUHE_UNFERTIG_AUFSCHUB_MS = 6000;

/*
 * ── Wortstille unter Ton: JEDER Satz wartet länger (26.10.2026, Ticket 3507 „Jev zuverlässig machen“) ──
 *
 * Martin, 26.10. (verärgert): Jev schickt mitten im Text ab, wenn er kurz überlegt. „Zuverlässigkeit geht vor Millisekunden.“
 * Beleg Journal 24.–26.10. (Konto 1, nur Zahlen; `[ptt-ende] … anlass=selbst-stille absenden=jev` gepaart mit `[ende-token]`):
 *   - 73 Sendungen durch Jev-Stille, 24 davon `entscheid=wortruhe`; bei 22 davon zeigte der Pegel noch Ton (`stillMs` 0–1870,
 *     Median ~410) — gesendet hat allein die Ruhe der Live-Mitschrift nach 2,5–6,3 s (meist Stufe 2/3 an `jevStillMs`).
 *   - Am 26.10. selbst 6 von 9 Jev-Sendungen über diesen Weg (`textRuhtMs` 2660/2850/2974/3233/4242/12037, `stillMs` 0–508).
 *   - Jevs Urteil über den ENDGÜLTIGEN Text (`[jev] fertig=`): nach Wortstille unter Ton Median 0,72, 10 von 20 unter 0,7
 *     (`textRuhtMs` 2516–6348, einmal 12037); nach Senden an der Stille des Pegels (`urteil`/`satz`) Median 0,89, 0 von 24 unter 0,7.
 *     Mit dem Aufschub unten hätten 9 der 10 nicht gesendet (der Fall 12037 lag schon im Aufschub von 17.10.).
 * Der Pegel zeigt Ton, die Mitschrift steht: das ist Nachdenken mit „ähm“/Atmen, eine hängende Live-Erkennung — oder Geräusch.
 * Unterscheiden lässt es sich nicht; Abschneiden ist schlimmer als Warten.
 *
 * Darum: zeigt der Pegel Ton (Stille am Pegel < WORTRUHE_TON_UNTER_MS), zählt die Ruhe des Textes bei JEDEM Satz erst nach
 * WORTRUHE_TON_AUFSCHUB_MS (bei klar unfertigem Satz wie bisher nach WORTRUHE_UNFERTIG_AUFSCHUB_MS). Die Wortstille sendet unter
 * Ton also frühestens 6,5 s nach dem letzten Wort (2,5 s + 4,0 s); die Obergrenzen rücken um denselben Aufschub: geschlossener
 * Satz 8,0 s, ohne Zeichen 8,0 s, nach Füllwort/Bindewort 10 s, bei klar unfertigem Satz (Jev < 0,2, Aufschub 6 s) 10–12 s
 * (Komma 11,5 s, Füllwort 12 s) — mit den Server-Schaltern von heute. Wird der Pegel still, gilt die Ruhe des Textes wieder
 * ganz — wie vorher.
 * Ausnahme: eine kurze Zusage (`kurzeZusage`: „Ja“, „Nein“, „Okay, weiter“ …) ist eine vollständige Antwort — für sie gilt
 * dieser Aufschub NICHT (sonst wartete ein „Ja“ unter Lüfter- oder TV-Ton 6,5 s statt 2,5 s; Prüfauflage 26.10.).
 * ⚠️ Der Notausgang gegen Dauerlärm (frontend/lib/jetzt-senden.ts `STILLE_NOTAUSGANG_MS`, 8 s) greift NUR, wenn der Pegel in
 *    jedem Bild mindestens DAUERLAERM_PEGEL (0,8) zeigt. Gegen leiseres Geräusch (unter 0,8, aber über der Grenze) hilft allein
 *    die Wortstille — mit den Obergrenzen oben, also bis 10–12 s.
 * ⚠️ Kosten: der Fall vom 15.10. (Geräusch ohne Wort nach fertigem Satz) wartet wieder länger (rund 6,5–8 s statt 2,5–4 s).
 * ⚠️ Nachstellen: nur diese Zahl. 0 = Verhalten bis 25.10.
 */
/** Unter Ton am Pegel zählt die Ruhe des Textes erst nach so viel — für jeden Satz (26.10.2026, Ticket 3507). */
export const WORTRUHE_TON_AUFSCHUB_MS = 4000;

/**
 * Wie viel der Ruhe des Textes zählt. Pegel still (≥ WORTRUHE_TON_UNTER_MS): die ganze. Pegel zeigt Ton: um
 * WORTRUHE_TON_AUFSCHUB_MS gekürzt — bei klar unfertigem Satz ohne Zeichen (`unfertigAn`, Schalter `STILLE_WORTRUHE_UNFERTIG`)
 * um WORTRUHE_UNFERTIG_AUFSCHUB_MS (Abschnitte oben). `fertig` `null` (kein Urteil): der Aufschub für jeden Satz.
 * Eine kurze Zusage (`kurzeZusage`) bekommt den Aufschub für jeden Satz nicht — für sie gilt alles wie bis 25.10.
 */
export function wortRuheZaehlt(ruhtMs: number, stillMs: number, text: string, fertig: number | null, unfertigAn = true): number {
  if (!(stillMs < WORTRUHE_TON_UNTER_MS)) return ruhtMs;
  const unfertig =
    unfertigAn && fertig !== null && Number.isFinite(fertig) && fertig < WORTRUHE_UNFERTIG_UNTER && satzEnde(text) === "keins";
  const ton = kurzeZusage(text) ? 0 : WORTRUHE_TON_AUFSCHUB_MS;
  return ruhtMs - Math.max(ton, unfertig ? WORTRUHE_UNFERTIG_AUFSCHUB_MS : 0);
}

/** Die Stille für die Regel: die des Pegels — oder, ruht der Text seit WORTRUHE_AB_MS, die größere der beiden. */
export function autoWortStille(stillMs: number, textRuhtMs: number | null | undefined): number {
  if (typeof textRuhtMs !== "number" || !Number.isFinite(textRuhtMs) || textRuhtMs < WORTRUHE_AB_MS) return stillMs;
  return Math.max(stillMs, textRuhtMs);
}

/**
 * Welche Fassung der Auto-Regel die App rechnet — für die Zeile `[ende-token]`
 * (`regelv=`): 1 feste Schwelle (29.09.), 2 Stufen (01.10.), 3 Satzzeichen
 * (02.10.), 4 Laut ohne neues Wort (05.10.), 5 Komma am Ende (07.10.), 6 Wortstille (15.10.), 7 Aufschub der
 * Wortstille bei klar unfertigem Satz unter Ton (17.10., `wortRuheZaehlt`), 8 Aufschub der Wortstille unter Ton für jeden Satz
 * (26.10., Ticket 3507, `WORTRUHE_TON_AUFSCHUB_MS`). Ob eine
 * Regel AN war, sagen ihre eigenen Felder (`regel=`, `satzregel=`, `textruhe=`;
 * für das Komma `kommaregel=` in `[stille-auto]`).
 */
export const AUTO_REGEL_VERSION = 8;

/**
 * Die Stille, an der Jevs Schwelle gemessen wird: die des Pegels — oder, bei
 * geschlossenem Satz, ab TEXTRUHE_AB_MS und wenn der Text um mindestens
 * TEXTRUHE_VORLAUF_MS länger steht, die Ruhe des Textes. `satz` `null`
 * (Satzzeichen-Regel aus) oder „keins“: immer die des Pegels.
 */
export function autoJevStille(stillMs: number, textRuhtMs: number | null | undefined, satz: SatzEnde | null | undefined): number {
  if (!satz || satz === "keins" || !(stillMs >= TEXTRUHE_AB_MS)) return stillMs;
  if (typeof textRuhtMs !== "number" || !Number.isFinite(textRuhtMs)) return stillMs;
  return textRuhtMs - stillMs >= TEXTRUHE_VORLAUF_MS ? textRuhtMs : stillMs;
}

/**
 * Die Satzzeichen-Stufen für diese Art — leer ohne Zeichen oder bei ausgeschalteter Regel (`null`). Mit `tempo` ≠ `ruhig`
 * (12.10.2026) kommen die Stufen aus `TEMPO_STUFEN` dazu; `ruhig` (und alles Unbekannte) liefert genau `SATZ_STUFEN`, dieselbe Liste wie vorher.
 */
function satzStufen(satz: SatzEnde | null | undefined, tempo: StilleTempo = "ruhig"): readonly SatzStufe[] {
  if (!satz || satz === "keins") return [];
  const t = tempoLesen(tempo);
  return t === "ruhig" ? SATZ_STUFEN[satz] : [...SATZ_STUFEN[satz], ...TEMPO_STUFEN[t][satz]];
}

/** Ab welcher Stille kann dieses Satzende frühestens senden (`AUTO_ERSTE_FRIST_MS` ohne Zeichen). */
export function autoFristMs(satz: SatzEnde | null | undefined, tempo: StilleTempo = "ruhig"): number {
  return Math.min(AUTO_ERSTE_FRIST_MS, ...satzStufen(satz, tempo).map((s) => s.abMs));
}

/**
 * Die Obergrenze für diese Lage — `AUTO_OFFEN_HOECHSTENS_MS` nur bei offenem Satz und klarem „nicht fertig“;
 * `komma` (07.10.2026: Komma am Ende und der Server sagt ja) geht vor: `AUTO_KOMMA_HOECHSTENS_MS`;
 * `deckelKurz` (12.10.2026, Schalter `STILLE_DECKEL_KURZ`, Standard aus): geschlossener Satz bei Jev < 0,5 →
 * `AUTO_GESCHLOSSEN_HOECHSTENS_MS`; `frageKurz` (16.10.2026, Schalter `STILLE_FRAGE_KURZ`, Standard an): „?“ bei Jev
 * 0,3 bis unter 0,5 → `AUTO_FRAGE_HOECHSTENS_MS`.
 */
export function autoHoechstensMs(
  fertig: number | null,
  satz: SatzEnde | null | undefined,
  komma = false,
  mittel = false,
  deckelKurz = false,
  frageKurz = false,
): number {
  if (komma) return AUTO_KOMMA_HOECHSTENS_MS;
  const bekannt = fertig !== null && Number.isFinite(fertig);
  const klarOffen = satz === "keins" && bekannt && fertig < AUTO_OFFEN_FERTIG_UNTER;
  if (klarOffen) return AUTO_OFFEN_HOECHSTENS_MS;
  // 16.10.2026: „?“ und Jev 0,3 bis unter 0,5 — `frageKurz` sagt der Server (STILLE_FRAGE_KURZ). Nie länger als vorher (4 s < 5 s < 6 s).
  if (frageKurz && satz === "?" && bekannt && fertig >= AUTO_FRAGE_KURZ_AB && fertig < AUTO_OFFEN_FERTIG_UNTER) {
    return AUTO_FRAGE_HOECHSTENS_MS;
  }
  // 12.10.2026: geschlossener Satz, Jev klar unter 0,5 — `deckelKurz` sagt der Server (STILLE_DECKEL_KURZ, Standard aus). Nie länger als vorher.
  if (deckelKurz && bekannt && fertig < AUTO_OFFEN_FERTIG_UNTER && (satz === "." || satz === "!" || satz === "?")) {
    return AUTO_GESCHLOSSEN_HOECHSTENS_MS;
  }
  // 08.10.2026: mittleres Urteil (0,5 bis unter AUTO_FERTIG_AB) — `mittel` sagt der Server (STILLE_MITTEL).
  if (mittel && bekannt && fertig >= AUTO_OFFEN_FERTIG_UNTER && fertig < AUTO_FERTIG_AB) {
    return satz === "." || satz === "!" ? AUTO_MITTEL_PUNKT_HOECHSTENS_MS : AUTO_MITTEL_HOECHSTENS_MS;
  }
  return AUTO_HOECHSTENS_MS;
}

/** Die feste Frist ohne Urteil — mit Satzzeichen kürzer. */
function autoRueckfallMs(satz: SatzEnde | null | undefined): number {
  return satzStufen(satz).length ? AUTO_SATZ_RUECKFALL_MS : AUTO_RUECKFALL_MS;
}

/** Eine Stufe: ab dieser Stille (ms) genügt diese Wahrscheinlichkeit für „fertig". */
interface AutoStufe {
  stufe: 1 | 2 | 3;
  abMs: number;
  fertigAb: number;
}

/** Die drei Stufen, aufsteigend — die EINE Tabelle für App und Server. */
export const AUTO_STUFEN: readonly AutoStufe[] = [
  { stufe: 1, abMs: AUTO_ERSTE_FRIST_MS, fertigAb: AUTO_FERTIG_AB },
  { stufe: 2, abMs: AUTO_STUFE_2_MS, fertigAb: AUTO_FERTIG_AB_2 },
  { stufe: 3, abMs: AUTO_STUFE_3_MS, fertigAb: AUTO_FERTIG_AB_3 },
];

/**
 * In welcher Stufe liegt diese Stille? 0 = vor der ersten Frist (es wird noch
 * nichts geprüft), 1…3 = die Stufen, 4 = die Obergrenze `AUTO_HOECHSTENS_MS`
 * (dann geht die Nachricht auf jeden Fall hinaus). Mit `stufen` = false gibt
 * es nur Stufe 1 (die feste Schwelle) und die Obergrenze.
 */
export function autoStufe(stillMs: number, stufen: boolean): 0 | 1 | 2 | 3 | 4 {
  if (!(stillMs >= AUTO_ERSTE_FRIST_MS)) return 0;
  if (stillMs >= AUTO_HOECHSTENS_MS) return 4;
  let stufe: 1 | 2 | 3 = 1;
  if (stufen) for (const s of AUTO_STUFEN) if (stillMs >= s.abMs) stufe = s.stufe;
  return stufe;
}

/**
 * Wie sicher muss Jev bei dieser Stille sein? — EINE Funktion für App und
 * Server. Vor der ersten Frist gilt die Schwelle von Stufe 1.
 *
 * `satz` (02.10.2026): das Satzende des Textes, wenn die Satzzeichen-Regel
 * gilt — `null`/fehlt: aus. Es gilt die NIEDRIGERE der beiden Schwellen; ein
 * Satzzeichen macht nie strenger.
 *
 * `ende` (16.10.2026): das offene Ende des Textes (`offenesEnde`), wenn der Server die Regel meldet — `null`/fehlt: aus.
 * Vor `OFFEN_STILLE_AB_MS` gilt dann mindestens `OFFEN_FERTIG_AB` bzw. (`anschluss`) gar keine Schwelle; macht nur strenger.
 */
export function autoSchwelle(
  stillMs: number,
  stufen: boolean,
  satz?: SatzEnde | null,
  tempo: StilleTempo = "ruhig",
  ende?: OffenesEnde | null,
): number {
  return offenSchwelle(satzSchwelle(stillMs, stufen, satz, tempo), stillMs, ende);
}

/** Die Schwelle aus Stufen und Satzzeichen-Stufen (bis 16.10.2026 die ganze `autoSchwelle`). */
function satzSchwelle(stillMs: number, stufen: boolean, satz: SatzEnde | null | undefined, tempo: StilleTempo): number {
  const mitSatz = satzStufen(satz, tempo);
  if (!mitSatz.length || !Number.isFinite(stillMs)) return heuteSchwelle(stillMs, stufen);
  /*
   * Vor der frühesten Frist gilt die Schwelle AN dieser Frist (wie ohne Zeichen
   * vor 1,5 s die von Stufe 1). Zwischen 1,2 und 1,5 s gilt NUR die Stufe des
   * Zeichens — die Regel von vorher sendet dort nie, ihre 0,8 darf die
   * strengere 0,85 nach „.“ nicht unterlaufen.
   */
  const ms = Math.max(stillMs, autoFristMs(satz, tempo));
  let schwelle = ms >= AUTO_ERSTE_FRIST_MS ? heuteSchwelle(ms, stufen) : Number.POSITIVE_INFINITY;
  for (const s of mitSatz) if (ms >= s.abMs) schwelle = Math.min(schwelle, s.fertigAb);
  return schwelle;
}

/** Die Schwelle ohne Satzzeichen-Regel (Stufen 2867). */
function heuteSchwelle(stillMs: number, stufen: boolean): number {
  let schwelle = AUTO_FERTIG_AB;
  if (stufen) for (const s of AUTO_STUFEN) if (stillMs >= s.abMs) schwelle = s.fertigAb;
  return schwelle;
}

/**
 * Ab welcher Stille schickt dieses Urteil ab? `null`: nie durch das Urteil —
 * dann gilt die Obergrenze. Für die Zeile in der Karte („wann hätte Jev
 * abgesendet"); entschieden wird immer an `autoSchwelle`.
 *
 * `ende` (16.10.2026, `offenesEnde`): eine Stufe vor `OFFEN_STILLE_AB_MS` zählt nur bei `offen` und ab `OFFEN_FERTIG_AB`;
 * sonst rückt sie auf `OFFEN_STILLE_AB_MS` (die Schwellen sinken mit der Stille — was vorher reichte, reicht dort auch).
 */
export function autoSendeAbMs(
  fertig: number,
  stufen: boolean,
  satz?: SatzEnde | null,
  tempo: StilleTempo = "ruhig",
  ende?: OffenesEnde | null,
): number | null {
  if (!Number.isFinite(fertig)) return null;
  const ab: number[] = [];
  for (const s of stufen ? AUTO_STUFEN : AUTO_STUFEN.slice(0, 1)) if (fertig >= s.fertigAb) ab.push(s.abMs);
  for (const s of satzStufen(satz, tempo)) if (fertig >= s.fertigAb) ab.push(s.abMs);
  if (!ab.length) return null;
  const frueh = ende === "offen" && fertig >= OFFEN_FERTIG_AB;
  return Math.min(...ab.map((ms) => (ende && !frueh ? Math.max(ms, OFFEN_STILLE_AB_MS) : ms)));
}

/**
 * Die festen Stufen für „Absenden nach Stille" (0 = aus, sonst ms) — EINE
 * Quelle für die Oberfläche (Einstellungen, Blatt der Antwortlänge) und den
 * Leser unten.
 *
 * 30.09.2026 (Ticket 2842): 4 und 5 s entfallen — Martin: *„genutzt werden
 * nur 2 oder 3 Sekunden oder Auto“*. Davor 0/2/3/4/5 s; 2 s bleibt die
 * Untergrenze der Absende-Regel (frontend/lib/jetzt-senden.ts, STILLE_MIN_MS).
 *
 * 17.10.2026 (Ticket 3176, Wunsch von Konto 2): dazu 10, 30 und 60 s — längere
 * Denkpausen beim Sprechen, ohne dass die Aufnahme abgeschickt wird. Gilt für
 * alle Konten; die Voreinstellung bleibt „aus" (DEFAULT_SETTINGS).
 *
 * 20.10.2026 (Martin, Regler „Automatisch absenden“): 5 s kommt zurück — „Feste Stille“ bietet 2, 3 und 5 s.
 */
export const SENDE_STILLE_STUFEN: readonly number[] = [0, 2000, 3000, 5000, 10_000, 30_000, 60_000];

/*
 * ── Automatisch absenden: Jev entscheidet / Feste Stille / Aus (20.10.2026) ──
 *
 * Martin, 20.10. 08:22: *„einen weiteren Regler … damit ich diesen Jev-Auto-Absender abschalten kann, oder wo ich
 * wählen kann: absenden nach zwei Sekunden Stille … wenn ein Satz abgeschlossen ist“*. Der Regler hat KEINEN eigenen
 * Speicher — er ist eine Sicht auf `sendeStilleMs` (EIN Besitzer, Ticket 2511): Auto (−1) = „jev“, 0 = „aus“, jede
 * feste Zahl = „fest“. Bei „fest“ und „aus“ wird Jev nie gefragt, also gelten auch STILLE_TEMPO (flott/blitz),
 * Fortsetzung und Zusage dort nicht.
 */
export type AbsendenModus = "jev" | "fest" | "aus";
/** Die Sekunden, die „Feste Stille“ zur Wahl stellt. */
export const FEST_STILLE_STUFEN: readonly number[] = [2000, 3000, 5000];
/** Was „Feste Stille“ beim Umschalten nimmt. */
export const FEST_STILLE_VORGABE_MS = 2000;

/** Der Modus zu einem gespeicherten `sendeStilleMs` — auch das Wort der Journalzeile `[ptt-ende] absenden=`. */
export function absendenModus(ms: number): AbsendenModus {
  if (ms === STILLE_AUTO_MS) return "jev";
  return ms > 0 ? "fest" : "aus";
}

/**
 * Wie lange die Live-Mitschrift mindestens unverändert stehen muss, bevor die Stille abschickt — in JEDEM Modus.
 * (20.10.2026, Martin: „zweimal mitten im Satz abgeschnitten“)
 *
 * Journal 20.10. 08:08–08:20 (Konto 1): von 9 Sendungen durch Stille kamen 6 mit `textRuhtMs` 21/334/507/733/753/966 —
 * die Erkennung lieferte also noch Wörter, als abgeschickt wurde. Der Pegel allein trägt das nicht: er kann sich irren
 * (Grenze über dem Anschlag, frontend/lib/mic-visualizer.ts GRENZE_HOECHSTENS), die Mitschrift ist der zweite Zeuge.
 * 800 ms: ein neues Wort kommt in Schüben rund alle 0,7 s (Messung 07.10., mic-visualizer.ts „Sprachgrenze“).
 * ⚠️ Ohne Live-Text (nativer Weg) gilt die Regel nicht — dort gibt es nichts, was ruhen könnte.
 */
export const STILLE_TEXTRUHE_MIN_MS = 800;
/** Die längste feste Stufe (60 s) — auf sie rückt alles, was gespeichert darüber liegt. */
export const STILLE_LAENGSTE_MS = Math.max(...SENDE_STILLE_STUFEN);

/**
 * Was ein gespeicherter Wert von `sendeStilleMs` heute gilt. (30.09.2026,
 * Ticket 2842)
 *
 * Wer vor dem Wegfall von 4 und 5 s eine davon gewählt hat (oder 7 s aus der
 * ersten Stufung), behält seine Wahl nicht als Wert ohne Knopf: sie rückt auf
 * die nächstkürzere Stufe, 3 s (seit Ticket 3176 gibt es darüber 10, 30 und
 * 60 s; ein Wert dazwischen, etwa 12 s, rückt auf 10 s, mehr als 60 s auf 60 s).
 * Sonst zeigte die Reihe eine andere Stufe, als die Stille wirklich schickt —
 * Anzeige und Wirklichkeit liefen auseinander. „Auto“ (−1), „aus“ (0) und
 * alles bis 3 s bleiben, wie sie gespeichert sind.
 *
 * ⚠️ EIN Leser, wie bei shared/vorlese-tempo.ts: Gerät (frontend/lib/
 *    settings.ts, loadSettings) und Server (server/settings-sanitize.ts)
 *    lesen durch diese Funktion. Ein Umzug im Speicher ist nicht nötig — der
 *    alte Wert liegt dort weiter, bis die Wahl einmal neu gesetzt wird.
 */
export function sendeStilleLesen(ms: number): number {
  if (ms > STILLE_LAENGSTE_MS) return STILLE_LAENGSTE_MS;
  // Ticket 3176: Werte ohne Knopf über 3 s (4, 5, 7 s, 12 s …) rücken auf die nächstkürzere Stufe.
  if (ms > 3000 && !SENDE_STILLE_STUFEN.includes(ms)) {
    return SENDE_STILLE_STUFEN.filter((s) => s <= ms).reduce((a, s) => Math.max(a, s), 0);
  }
  return ms;
}

/** Die Stufe in einem Wort: „Aus", „Auto" oder „3 s" — für Schalter und Einstellungen. */
export function stilleKurz(ms: number): string {
  if (ms === STILLE_AUTO_MS) return "Auto";
  return ms > 0 ? `${Math.round(ms / 1000)} s` : "Aus";
}

/** Was aus einer Antwort von Jev folgt — auch das Wort der Journalzeile. */
export type AutoEntscheid = "senden" | "warten" | "rueckfall";

/**
 * `fertig` → Entscheid. `null` (Jev scheiterte oder kam zu spät) = Rückfall.
 *
 * `stillMs` und `stufen` (Ticket 2867): die Schwelle sinkt mit der Stille
 * (`autoSchwelle`). Ohne Angabe gilt die Stille der ersten Frist und keine
 * Stufen — die feste Schwelle `AUTO_FERTIG_AB` wie bisher.
 */
export function autoEntscheid(
  fertig: number | null,
  stillMs: number = AUTO_ERSTE_FRIST_MS,
  stufen = false,
  satz?: SatzEnde | null,
  tempo: StilleTempo = "ruhig",
  ende?: OffenesEnde | null,
): AutoEntscheid {
  if (fertig === null || !Number.isFinite(fertig)) return "rueckfall";
  return fertig >= autoSchwelle(stillMs, stufen, satz, tempo, ende) ? "senden" : "warten";
}

/** „2,5 s" — Sekunden mit Komma, ohne überflüssige Nullen. */
function sekunden(ms: number): string {
  return `${String(Math.round(ms / 100) / 10).replace(".", ",")} s`;
}

/**
 * Die kleine Zeile in der Aufnahmekarte: was Jev auf die Frage „fertig?“
 * geantwortet hat und was daraus folgt. (30.09.2026, Ticket 2842)
 *
 * Martin will sehen, ob und wann Jev abgesendet hätte. Gezeigt wird nur, was
 * der Server geliefert hat: `fertig` (zwei Stellen, wie im Journal) und der
 * Entscheid daraus, den die App selbst nach derselben Regel trifft
 * (`autoSendeAbMs`, aus derselben Stufentabelle wie `autoSchwelle`). Ohne
 * Antwort steht keine Zahl da, nur die feste Frist, die dann gilt.
 *
 * Mit `stufen` (Ticket 2867) sagt die Zeile auch WANN: „senden nach 2,5 s“
 * heißt, die Schwelle dieser Stufe ist erreicht, sobald es so lange still ist.
 */
export function jevUrteilZeile(
  fertig: number | null,
  stufen = false,
  satz?: SatzEnde | null,
  tempo: StilleTempo = "ruhig",
  ende?: OffenesEnde | null,
): string {
  const e = autoEntscheid(fertig);
  if (e === "rueckfall" || fertig === null) return `Jev: keine Antwort · feste ${sekunden(autoRueckfallMs(satz))}`;
  const zahl = fertig.toFixed(2).replace(".", ",");
  const ab = autoSendeAbMs(fertig, stufen, satz, tempo, ende);
  if (ab === null) return `Jev: noch nicht fertig ${zahl} · weiter zuhören`;
  if (ab <= AUTO_ERSTE_FRIST_MS) return `Jev: fertig ${zahl} · senden`;
  return `Jev: wohl fertig ${zahl} · senden nach ${sekunden(ab)}`;
}

/** Jevs Urteil über genau diesen Text. */
export interface AutoUrteil {
  text: string;
  /** `null` = keine brauchbare Antwort (Fehler, Frist, kein Zugang). */
  fertig: number | null;
  /**
   * Gilt die gestufte Schwelle (Ticket 2867)? Der Server sagt es mit seiner
   * Antwort (Schalter `STILLE_STUFEN_AUS`); fehlt die Angabe, gilt die feste
   * Schwelle — im Zweifel das alte Verhalten.
   */
  stufen?: boolean;
  /**
   * Gilt die Satzzeichen-Regel (02.10.2026)? Der Server sagt es mit seiner
   * Antwort (Schalter `STILLE_SATZZEICHEN`); fehlt die Angabe, gilt sie
   * nicht — im Zweifel das alte Verhalten.
   */
  satzzeichen?: boolean;
  /**
   * Gilt „Laut ohne neues Wort“ (05.10.2026, `autoJevStille`)? Der Server sagt
   * es mit seiner Antwort (Schalter `STILLE_TEXTRUHE`); nur ein ausdrückliches
   * `true` zählt.
   */
  textruhe?: boolean;
  /**
   * Gilt „Komma am Ende“ (07.10.2026, `kommaAmEnde`)? Der Server sagt es mit seiner
   * Antwort (Schalter `STILLE_KOMMA`); nur ein ausdrückliches `true` zählt.
   */
  komma?: boolean;
  /** 08.10.2026: gilt „mittleres Urteil“ (`AUTO_MITTEL_HOECHSTENS_MS`)? Schalter `STILLE_MITTEL`; nur ein ausdrückliches `true` zählt. */
  mittel?: boolean;
  /**
   * 12.10.2026: gilt der Feinschliff (die App wertet Jevs Antwort sofort aus und fragt bei geschlossenem Satz
   * früher)? Schalter `STILLE_FEIN` (Standard an); nur ein ausdrückliches `true` zählt.
   */
  fein?: boolean;
  /**
   * 12.10.2026: gilt der kurze Deckel `AUTO_GESCHLOSSEN_HOECHSTENS_MS` (geschlossener Satz, Jev < 0,5)? Schalter
   * `STILLE_DECKEL_KURZ` (Standard AUS); nur ein ausdrückliches `true` zählt.
   */
  deckelKurz?: boolean;
  /** 16.10.2026: gilt `AUTO_FRAGE_HOECHSTENS_MS` („?“, Jev 0,3–0,49)? Schalter `STILLE_FRAGE_KURZ` (Standard an); nur ein ausdrückliches `true` zählt. */
  frageKurz?: boolean;
  /** 16.10.2026: gilt „offenes Ende“ (`offenesEnde`, ohne Satzzeichen strenger)? Schalter `STILLE_OFFEN_STRENG` (Standard an); nur ein ausdrückliches `true` zählt. */
  offenStreng?: boolean;
  /** 16.10.2026: gilt die kurze Obergrenze (`obergrenzeKurzMs`, 4 s / 6 s)? Schalter `STILLE_OBERGRENZE_KURZ` (Standard an); nur ein ausdrückliches `true` zählt. */
  obergrenzeKurz?: boolean;
  /**
   * 12.10.2026: welche Tempo-Stufe gilt (`TEMPO_STUFEN`)? Schalter `STILLE_TEMPO` am Server (Standard `ruhig`); die App nimmt
   * `flott`/`blitz` nur, wenn dieselbe Antwort die Fortsetzung meldet. Nur ein ausdrückliches „flott“ oder „blitz“ zählt (`tempoLesen`).
   */
  tempo?: StilleTempo;
  /** 15.10.2026: gilt „kurze Zusage“ (`kurzeZusage`)? Schalter `STILLE_ZUSAGE` (Standard an); nur ein ausdrückliches `true` zählt. */
  zusage?: boolean;
  /** 15.10.2026: gilt „Wortstille“ (`autoWortStille`)? Schalter `STILLE_WORTRUHE` (Standard an); nur ein ausdrückliches `true` zählt. */
  wortruhe?: boolean;
  /** 17.10.2026: gilt der Aufschub der Wortstille bei klar unfertigem Satz (`wortRuheZaehlt`)? Schalter `STILLE_WORTRUHE_UNFERTIG` (Standard an); nur ein ausdrückliches `true` zählt. */
  wortruheUnfertig?: boolean;
}

/** Was die App von der Frage zurückbekommt: das Urteil und, ob die Stufen und die Satzzeichen-Regel gelten. */
export interface AutoAntwort {
  fertig: number | null;
  stufen: boolean;
  satzzeichen?: boolean;
  /** 05.10.2026: gilt „Laut ohne neues Wort“? Nur ein ausdrückliches `true` zählt. */
  textruhe?: boolean;
  /** 07.10.2026: gilt „Komma am Ende“ (`kommaAmEnde`)? Nur ein ausdrückliches `true` zählt. */
  komma?: boolean;
  /** 08.10.2026: gilt „mittleres Urteil“ (`AUTO_MITTEL_HOECHSTENS_MS`)? Nur ein ausdrückliches `true` zählt. */
  mittel?: boolean;
  /** 12.10.2026: gilt der Feinschliff (frühe Frage bei geschlossenem Satz, sofortige Auswertung)? `false` und `true` kommen vom Server. */
  fein?: boolean;
  /** 12.10.2026: gilt der kurze Deckel (`AUTO_GESCHLOSSEN_HOECHSTENS_MS`)? Standard am Server AUS; nur ein ausdrückliches `true` zählt. */
  deckelKurz?: boolean;
  /** 16.10.2026: gilt `AUTO_FRAGE_HOECHSTENS_MS` („?“, Jev 0,3–0,49)? Standard am Server an; nur ein ausdrückliches `true` zählt. */
  frageKurz?: boolean;
  /** 16.10.2026: gilt „offenes Ende“ (`offenesEnde`)? Standard am Server an; nur ein ausdrückliches `true` zählt. */
  offenStreng?: boolean;
  /** 16.10.2026: gilt die kurze Obergrenze (`obergrenzeKurzMs`)? Standard am Server an; nur ein ausdrückliches `true` zählt. */
  obergrenzeKurz?: boolean;
  /**
   * 12.10.2026: welche Tempo-Stufe gilt? `ruhig` (Standard) | `flott` | `blitz`. Der Server schickt immer `ruhig`, wenn die Fortsetzung aus ist.
   * Die App liest es mit `tempoLesen` und nur zusammen mit `fortsetzung: true`.
   */
  tempo?: StilleTempo;
  /**
   * 12.10.2026: gilt die Fortsetzung (Schalter `FORTSETZUNG`, Standard an)? Dann gibt die App eine neue Aufnahme binnen
   * `FORTSETZUNG_FENSTER_MS` nach einem Auto-Senden als Fortsetzung der vorigen Äußerung an (shared/fortsetzung.ts). Nur ein ausdrückliches `true` zählt.
   */
  fortsetzung?: boolean;
  /** 15.10.2026: gilt „kurze Zusage“ (`kurzeZusage`)? `false` und `true` kommen vom Server, die App merkt sich die letzte (wie `fein`). */
  zusage?: boolean;
  /** 15.10.2026: gilt „Wortstille“ (`autoWortStille`)? `false` und `true` kommen vom Server, die App merkt sich die letzte (wie `zusage`). */
  wortruhe?: boolean;
  /** 17.10.2026: gilt der Aufschub der Wortstille bei klar unfertigem Satz (`wortRuheZaehlt`)? Nur ein ausdrückliches `true` zählt. */
  wortruheUnfertig?: boolean;
  /**
   * 07.10.2026: darf die Anzeige der Karte die Sprachgrenze anwenden (frontend/lib/
   * mic-visualizer.ts, `SPRACH_ABSTAND`; Schalter `STILLE_SPRACHGRENZE`)? Nur ein
   * ausdrückliches `true` zählt.
   */
  sprachgrenze?: boolean;
  /**
   * 03.10.2026: hat der Server Fantasiewörter am Ende fürs Urteil weggelassen
   * (shared/rausch-woerter.ts, Schalter `STILLE_RAUSCH_FILTER`)? Nur ein
   * ausdrückliches `true` zählt.
   */
  rauschFilter?: boolean;
}

export interface AutoLage {
  /** Die Mitschrift bis jetzt — leer auf dem nativen Weg. */
  text: string;
  /** Wie lange es schon still ist. */
  stillMs: number;
  /** Kam in dieser Aufnahme schon Stimme? Vorher schickt nichts ab. */
  gesprochen: boolean;
  /** Das letzte Urteil — gilt nur, wenn sein Text der jetzige ist. */
  urteil: AutoUrteil | null;
  /** Läuft eine Frage für DIESEN Text: seit wie vielen ms. Sonst `null`. */
  fragtSeitMs: number | null;
  /**
   * 05.10.2026: wie lange der Text schon unverändert steht (`autoJevStille`).
   * Fehlt die Zahl, gilt allein die Stille des Pegels — wie vorher.
   */
  textRuhtMs?: number | null;
  /**
   * 12.10.2026: hat der Server den Feinschliff gemeldet (die letzte Antwort sagte `fein: true`)? Nur dann
   * wird bei geschlossenem Satz schon ab `AUTO_FRAGE_AB_GESCHLOSSEN_MS` gefragt. Fehlt es: 1,0 s wie vorher.
   */
  fein?: boolean;
  /**
   * 15.10.2026: hat der Server „kurze Zusage“ gemeldet (die letzte Antwort sagte `zusage: true`)? Dann geht eine
   * kurze Zusage (`kurzeZusage`) nach AUTO_ZUSAGE_AB_MS hinaus, auch ohne Urteil für genau diesen Text. Fehlt es,
   * gilt die Regel nur mit einem Urteil für genau diesen Text, das `zusage: true` trägt.
   */
  zusage?: boolean;
  /**
   * 15.10.2026: hat der Server „Wortstille“ gemeldet (die letzte Antwort sagte `wortruhe: true`)? Gilt, solange es kein
   * Urteil für genau diesen Text gibt (fragen, Rückfall); mit Urteil zählt dessen `wortruhe`.
   */
  wortruhe?: boolean;
  /**
   * 17.10.2026: war eine Frage offen (das Mikro ging nach `nach-frage` auf)? Dann geht eine kurze Zusage schon nach
   * AUTO_ZUSAGE_FRAGE_AB_MS hinaus — außer der Server meldete `zusage: false` (Schalter `STILLE_ZUSAGE=0`).
   */
  frageOffen?: boolean;
}

/**
 * Wodurch eine Nachricht hinausgeht: Jevs Urteil, die Obergrenze, die feste
 * Frist ohne Jev — oder `satz`: die Satzzeichen-Regel schickte FRÜHER ab, als
 * es die Regel ohne sie getan hätte (mit Urteil oder als kürzere Frist ohne) —
 * oder `textruhe` (05.10.2026): erst die Ruhe des Textes über einen Laut ohne
 * neues Wort hinweg erreichte Jevs Schwelle (`autoJevStille`) — oder `komma`
 * (07.10.2026): die Obergrenze bei Komma am Ende kam vor der von vorher — oder `geschlossen`
 * (12.10.2026): die Obergrenze 5,0 s für einen geschlossenen Satz bei Jev < 0,5 kam vor der von vorher (seit 16.10.2026 auch
 * 4,0 s nach „?“ bei Jev 0,3–0,49, `AUTO_FRAGE_HOECHSTENS_MS`) — oder `tempo`
 * (12.10.2026): erst eine Stufe von `flott`/`blitz` (`TEMPO_STUFEN`) erreichte Jevs Schwelle; mit `ruhig` hätte es noch gewartet — oder
 * `zusage` (15.10.2026): der ganze Text war eine kurze Zusage (`kurzeZusage`) und AUTO_ZUSAGE_AB_MS Stille vergangen — oder
 * `wortruhe` (15.10.2026): erst die Ruhe des Textes trotz Ton am Pegel schickte ab (`autoWortStille`, Obergrenze nach dem letzten Wort) — oder
 * `obergrenze` (16.10.2026): die kurze Obergrenze (`obergrenzeKurzMs`, 4 s / 6 s) kam vor jeder Grenze von vorher.
 */
export type AutoGrund =
  | "urteil" | "hoechstens" | "rueckfall" | "satz" | "textruhe" | "komma" | "mittel" | "geschlossen" | "tempo" | "zusage" | "wortruhe"
  | "obergrenze";

/** Der Schritt mit seinem Grund und der Stufe, in der er fiel (`autoStufe`; ohne Jev-Urteil 0). */
export interface AutoPruefung {
  schritt: "senden" | "fragen" | "warten";
  /** Nur beim Senden. */
  grund: AutoGrund | null;
  /** Nur beim Senden: die Stufe der Stille am PEGEL (1…3) oder 4 = Obergrenze; sonst 0. */
  stufe: 0 | 1 | 2 | 3 | 4;
  /** Nur bei `grund: "textruhe"` oder `"wortruhe"`: die Stille, an der Jevs Schwelle gemessen wurde (ms). */
  jevStillMs?: number;
}

/**
 * Was jetzt zu tun ist: abschicken, Jev fragen oder weiter warten — samt
 * Grund und Stufe für das Journal. Rein — die Frage selbst stellt der Aufrufer.
 *
 * Das Urteil gilt nur für den Text, für den es gestellt wurde; Jev wird je
 * Text einmal gefragt. Die Schwelle, an der das Urteil gemessen wird, sinkt
 * mit der Stille (`autoSchwelle`) — jeder Takt der App ruft hier neu an.
 */
export function autoPruefung(l: AutoLage): AutoPruefung {
  const p = autoPruefungPegel(l);
  if (p.schritt === "senden") return p;
  // 15.10.2026 („Wortstille“, unten): der Text ruht, der Pegel zeigt Ton — dann zählt die Ruhe des Textes als Stille.
  const text = l.text.trim();
  const urteil = text && l.urteil?.text === text ? l.urteil : null;
  const roh = l.textRuhtMs;
  if (!l.gesprochen || text === "" || typeof roh !== "number" || !Number.isFinite(roh)) return p;
  // 17.10.2026: klar unfertiger Satz ohne Zeichen unter Ton — die Ruhe des Textes zählt erst nach dem Aufschub (`wortRuheZaehlt`).
  // 26.10.2026 (Ticket 3507): unter Ton zählt sie bei JEDEM Satz erst nach WORTRUHE_TON_AUFSCHUB_MS.
  const ruht = wortRuheZaehlt(roh, l.stillMs, text, urteil?.fertig ?? null, urteil?.wortruheUnfertig === true);
  // Senden nur mit Ja des Servers (Urteil für genau diesen Text, sonst die letzte Antwort). FRAGEN immer: eine Frage
  // schickt nichts ab, und erst ihre Antwort sagt einer frisch geladenen Seite, ob die Regel gilt.
  const gilt = urteil ? urteil.wortruhe === true : l.wortruhe === true;
  const still = autoWortStille(l.stillMs, ruht);
  if (still > l.stillMs) {
    // 26.10.2026: auch die Brücke „Laut ohne neues Wort“ (`autoJevStille`) sieht nur die gezählte Ruhe — sonst unterliefe sie den Aufschub.
    const w = autoPruefungPegel({ ...l, stillMs: still, textRuhtMs: ruht });
    if (w.schritt === "senden" && gilt) return { schritt: "senden", grund: "wortruhe", stufe: w.stufe, jevStillMs: Math.round(still) };
    if (w.schritt === "fragen" && p.schritt === "warten") return w;
  }
  // 26.10.2026: GEFRAGT wird weiter an der ganzen Ruhe des Textes (eine Frage schickt nichts ab) — nur das Senden wartet den Aufschub ab.
  const stillRoh = autoWortStille(l.stillMs, roh);
  if (p.schritt === "warten" && stillRoh > still && autoPruefungPegel({ ...l, stillMs: stillRoh }).schritt === "fragen") {
    return { schritt: "fragen", grund: null, stufe: 0 };
  }
  if (!gilt) return p;
  // Die harte Obergrenze nach dem letzten Wort: geschlossener Satz, Jev ab AUTO_OFFEN_FERTIG_UNTER (0,5).
  const fertig = urteil?.fertig ?? null;
  if (
    ruht >= WORTRUHE_GESCHLOSSEN_HOECHSTENS_MS &&
    fertig !== null &&
    Number.isFinite(fertig) &&
    fertig >= AUTO_OFFEN_FERTIG_UNTER &&
    satzEnde(text) !== "keins"
  ) {
    return { schritt: "senden", grund: "wortruhe", stufe: 4, jevStillMs: Math.round(ruht) };
  }
  return p;
}

/**
 * Das offene Ende eines Urteils (`offenesEnde` seines Textes) — nur, wenn der Server die Regel dafür meldete
 * (`offenStreng: true`); sonst `null` (alles wie vorher). EINE Stelle für Entscheid und Anzeige der App.
 */
export function urteilEnde(u: Pick<AutoUrteil, "text" | "offenStreng"> | null | undefined): OffenesEnde | null {
  return u?.offenStreng === true ? offenesEnde(u.text) : null;
}

/**
 * Die Obergrenze von vorher (`autoHoechstensMs`) für ein Urteil über genau diesen Text — mit den Schaltern, die das Urteil
 * trägt. `urteil` `null`: die Grenze ohne Urteil (6 s).
 */
function urteilHoechstensMs(text: string, urteil: AutoUrteil | null): number {
  const satz = urteil?.satzzeichen === true ? satzEnde(text) : null;
  const komma = urteil?.komma === true && kommaAmEnde(text);
  return autoHoechstensMs(urteil?.fertig ?? null, satz, komma, urteil?.mittel === true, urteil?.deckelKurz === true, urteil?.frageKurz === true);
}

/**
 * Die Regel allein an der Stille des Pegels (bis 15.10.2026 die ganze `autoPruefung`). 16.10.2026: sendet die Regel von
 * vorher (`autoPruefungBisher`) nicht, gilt danach die kurze Obergrenze (`obergrenzeKurzMs`) — so behält jeder frühere Weg
 * seinen Grund, und `obergrenze` steht nur da, wo erst sie sendete.
 */
function autoPruefungPegel(l: AutoLage): AutoPruefung {
  const p = autoPruefungBisher(l);
  if (p.schritt === "senden" || !l.gesprochen) return p;
  const text = l.text.trim();
  const urteil = text && l.urteil?.text === text ? l.urteil : null;
  if (urteil?.obergrenzeKurz !== true || l.stillMs < obergrenzeKurzMs(urteilHoechstensMs(text, urteil), text)) return p;
  return { schritt: "senden", grund: "obergrenze", stufe: autoStufe(l.stillMs, urteil.stufen === true) };
}

/** Die Regel an der Stille des Pegels ohne die kurze Obergrenze (bis 16.10.2026 `autoPruefungPegel`). */
function autoPruefungBisher(l: AutoLage): AutoPruefung {
  const warten: AutoPruefung = { schritt: "warten", grund: null, stufe: 0 };
  if (!l.gesprochen) return warten;
  const text = l.text.trim();
  // Das Urteil für GENAU diesen Text — nur mit ihm gilt die Satzzeichen-Regel (der Server sagt, ob sie an ist).
  const urteil = text && l.urteil?.text === text ? l.urteil : null;
  const satz = urteil?.satzzeichen === true ? satzEnde(text) : null;
  // 07.10.2026: Komma am Ende — die kürzere Obergrenze, nur mit Urteil für genau diesen Text und Ja des Servers.
  const komma = urteil?.komma === true && kommaAmEnde(text);
  // 08.10.2026: mittleres Urteil — kürzere Obergrenze, nur mit Urteil für genau diesen Text und Ja des Servers.
  const mittel = urteil?.mittel === true;
  // 12.10.2026: kurzer Deckel — kürzere Obergrenze für einen geschlossenen Satz bei Jev < 0,5; nur mit Urteil für genau diesen Text und Ja des Servers (Standard dort: aus).
  const deckelKurz = urteil?.deckelKurz === true;
  // 16.10.2026: Frage bei unsicherem Urteil — kürzere Obergrenze nach „?“ bei Jev 0,3–0,49; nur mit Urteil für genau diesen Text und Ja des Servers.
  const frageKurz = urteil?.frageKurz === true;
  // 12.10.2026: Tempo-Stufe — nur mit Urteil für genau diesen Text und ausdrücklichem „flott“/„blitz“ des Servers; sonst `ruhig` (alles wie vorher).
  const tempo = tempoLesen(urteil?.tempo);
  // 16.10.2026: offenes Ende (ohne Satzzeichen strenger) — nur mit Urteil für genau diesen Text und Ja des Servers; macht nur später, nie früher.
  const ende = urteilEnde(urteil);
  const fertigUrteil = urteil?.fertig ?? null;
  if (l.stillMs >= urteilHoechstensMs(text, urteil)) {
    // Hätte die Obergrenze von vorher jetzt auch gesendet? Sonst war es das Komma, das mittlere Urteil bzw. der geschlossene Satz.
    if (l.stillMs >= autoHoechstensMs(fertigUrteil, satz)) return { schritt: "senden", grund: "hoechstens", stufe: 4 };
    const stufe = autoStufe(l.stillMs, urteil?.stufen === true);
    // Der kurze Deckel (5 s) und die Frage bei unsicherem Urteil (4 s, 16.10.2026) heißen beide `geschlossen` — `satzzeichen=` und `jev=` in `[ende-token]` trennen sie.
    if ((deckelKurz || frageKurz) && l.stillMs < autoHoechstensMs(fertigUrteil, satz, komma, mittel)) return { schritt: "senden", grund: "geschlossen", stufe };
    return { schritt: "senden", grund: komma ? "komma" : "mittel", stufe };
  }
  const rueckfall = (): AutoPruefung => {
    if (l.stillMs >= AUTO_RUECKFALL_MS) return { schritt: "senden", grund: "rueckfall", stufe: 0 };
    return l.stillMs >= autoRueckfallMs(satz) ? { schritt: "senden", grund: "satz", stufe: 0 } : warten;
  };
  if (!text) return rueckfall();
  // 15.10.2026: eine kurze Zusage („Okay, und weiter“) geht nach 1,2 s hinaus, gleich, was Jev sagt — nur mit Ja des Servers
  // (die letzte Antwort oder das Urteil für genau diesen Text). Jedes andere Wort im Text: alles wie vorher.
  if ((l.zusage === true || urteil?.zusage === true) && l.stillMs >= AUTO_ZUSAGE_AB_MS && kurzeZusage(text)) {
    return { schritt: "senden", grund: "zusage", stufe: autoStufe(l.stillMs, urteil?.stufen === true) };
  }
  // 17.10.2026: offene Frage + kurze Zusage → sofort (nach einer Atempause), solange der Server `zusage` nicht ausdrücklich abschaltete.
  if (l.frageOffen === true && l.zusage !== false && l.stillMs >= AUTO_ZUSAGE_FRAGE_AB_MS && kurzeZusage(text)) {
    return { schritt: "senden", grund: "zusage", stufe: autoStufe(l.stillMs, urteil?.stufen === true) };
  }
  if (l.stillMs < autoFristMs(satz, tempo)) {
    // Nur FRAGEN, nie senden: für diesen Text gibt es noch kein Urteil und keine laufende Frage.
    const keinUrteil = urteil === null && l.fragtSeitMs === null;
    // 12.10.2026: bei geschlossenem Satz (`satzEnde`, unabhängig vom Urteil) und Ja des Servers (`l.fein`) schon ab AUTO_FRAGE_AB_GESCHLOSSEN_MS; die Sendeschwellen bleiben.
    return keinUrteil && l.stillMs >= autoFrageAbMs(satzEnde(text), l.fein === true) ? { schritt: "fragen", grund: null, stufe: 0 } : warten;
  }
  if (urteil) {
    const stufen = urteil.stufen === true;
    // 05.10.2026: ein Laut ohne neues Wort setzt Jevs Uhr nicht zurück — nur mit Satzzeichen und nur, wenn der Server es sagt.
    const jevStill = urteil.textruhe === true ? autoJevStille(l.stillMs, l.textRuhtMs, satz) : l.stillMs;
    const e = autoEntscheid(urteil.fertig, jevStill, stufen, satz, tempo, ende);
    if (e === "rueckfall") return rueckfall();
    if (e === "warten") return warten;
    const stufe = autoStufe(l.stillMs, stufen);
    // Hätte die Stille am Pegel allein jetzt auch gesendet? Sonst war es die Ruhe des Textes.
    if (jevStill > l.stillMs && autoEntscheid(urteil.fertig, l.stillMs, stufen, satz, tempo) !== "senden") {
      return { schritt: "senden", grund: "textruhe", stufe, jevStillMs: Math.round(jevStill) };
    }
    // 12.10.2026: Hätte `ruhig` (die Stufen von vorher) jetzt auch gesendet? Sonst war es die Tempo-Stufe. (Vor der Frist von `ruhig` sendet
    // `ruhig` nie — `autoSchwelle` rechnet dort mit der Schwelle AN der Frist, das Tor davor steht in dieser Funktion.)
    if (tempo !== "ruhig" && (l.stillMs < autoFristMs(satz) || autoEntscheid(urteil.fertig, l.stillMs, stufen, satz) !== "senden")) {
      return { schritt: "senden", grund: "tempo", stufe };
    }
    // Hätte die Regel OHNE Satzzeichen jetzt auch gesendet? Sonst war es das Zeichen.
    const ohneSatz = l.stillMs >= AUTO_ERSTE_FRIST_MS && autoEntscheid(urteil.fertig, l.stillMs, stufen) === "senden";
    return { schritt: "senden", grund: ohneSatz ? "urteil" : "satz", stufe };
  }
  if (l.fragtSeitMs !== null) {
    return l.fragtSeitMs >= AUTO_ANTWORT_FRIST_MS ? rueckfall() : warten;
  }
  return { schritt: "fragen", grund: null, stufe: 0 };
}

/** Nur der Schritt — die Kurzform von `autoPruefung`. */
export function autoSchritt(l: AutoLage): "senden" | "fragen" | "warten" {
  return autoPruefung(l).schritt;
}

/** Auflösung und Reichweite von `autoRestMs`: die längste Obergrenze ist `AUTO_OFFEN_HOECHSTENS_MS` (9 s; mit kurzer Obergrenze 6 s). */
const AUTO_REST_SCHRITT_MS = 10;
const AUTO_REST_BIS_MS = AUTO_OFFEN_HOECHSTENS_MS + 1000;

/**
 * Wie lange hätte Auto in dieser Lage noch gebraucht, bis es abschickt? (12.10.2026, Messfühler für
 * `[ptt-ende] senden … autoRestMs=`.) Gerechnet wird mit derselben Regel (`autoPruefung`), als bliebe
 * es still und der Text unverändert — gefragt wird dabei nicht, es zählt Jevs vorliegendes Urteil.
 * `null`: kein Urteil für genau diesen Text oder noch nicht gesprochen. 0: Auto hätte in diesem Moment
 * ohnehin abgeschickt. Auf 10 ms genau.
 *
 * ⚠️ Läuft im Moment des Absendens (Knopf, Wort …): darum gesucht wird halbierend (rund 10 Prüfungen), nicht
 *    Schritt für Schritt (1.000 Prüfungen; bei einem Text von 3.000 Zeichen gemessen 15 statt rund 0,2 ms). Das
 *    geht, weil die Regel bei gleichem Urteil und wachsender Stille nie von „senden“ zurück auf „warten“ fällt
 *    (Schwellen sinken, Obergrenzen und Rückfall liegen fest; Test: tests/shared/stille-feinschliff.test.ts
 *    vergleicht mit dem Schritt-für-Schritt-Lauf über Texte, Urteile und Startstillen).
 */
export function autoRestMs(l: AutoLage): number | null {
  const text = l.text.trim();
  if (!l.gesprochen || !text || l.urteil?.text !== text || !Number.isFinite(l.stillMs)) return null;
  const sendetNach = (d: number): boolean =>
    autoPruefung({
      ...l,
      stillMs: l.stillMs + d,
      textRuhtMs: typeof l.textRuhtMs === "number" ? l.textRuhtMs + d : l.textRuhtMs,
      fragtSeitMs: null,
    }).schritt === "senden";
  if (sendetNach(0)) return 0;
  let zu = 0; // sendet noch nicht
  let ab = AUTO_REST_BIS_MS; // sendet
  if (!sendetNach(ab)) return null;
  while (ab - zu > AUTO_REST_SCHRITT_MS) {
    const mitte = zu + Math.floor((ab - zu) / (2 * AUTO_REST_SCHRITT_MS)) * AUTO_REST_SCHRITT_MS;
    if (sendetNach(mitte)) ab = mitte;
    else zu = mitte;
  }
  return ab;
}

/* ── Der Stand für das Blatt der Antwortlänge (29.09.2026) ─────────────────
 *
 * Martin, 29.09. ~22:00: *„… ob der Auto-Modus an sein soll oder nicht und
 * inwieweit er jetzt schon aktiv ist."* Der Server zählt je Konto und Wiener
 * Kalendertag mit (server/stille-auto-zaehler.ts), die App liest den Stand
 * über GET STILLE_AUTO_STAND_PFAD. Die Worte der Zeile stehen HIER, für App
 * und Prüfung dieselben.
 */

/** Wo die App den Stand liest (nur Konto 1, echte Sitzung). */
export const STILLE_AUTO_STAND_PFAD = "/api/stille-auto/stand";
/**
 * So lange nach einem Ausfall gilt Jev als „gerade nicht erreichbar" — es
 * sei denn, er hat seitdem wieder geantwortet. Länger her: ein Ausfall von
 * vorhin, keine Störung.
 */
export const AUTO_STOERUNG_FENSTER_MS = 10 * 60_000;

/** Die drei Zähler eines Tages. */
type AutoTagesZahlen = Record<AutoEntscheid, number>;

/** Was der Server über Auto weiß — so steht es in der Antwort des Lese-Wegs. */
export interface AutoStand {
  /** Der Wiener Kalendertag der Zahlen, „2026-09-29". */
  tag: string;
  heute: AutoTagesZahlen;
  /** Wann Jev zuletzt eine brauchbare Antwort gab (Unix-ms) — `null`: noch nie. */
  letzteAntwortMs: number | null;
  /** Wann Jev zuletzt ausfiel (Unix-ms, kein Text) — `null`: noch nie. */
  letzterAusfallMs: number | null;
  /** Jev antwortet gerade nicht: der letzte Ausfall ist frisch und ohne Antwort danach. */
  stoerung: boolean;
}

/** Was die Zeile zeigen kann: der Stand, „wird geholt" oder „nicht abrufbar". */
export type AutoStandLage = AutoStand | "laedt" | "fehlt";

/** Ist Jev gerade nicht erreichbar? Rein — die Uhr kommt vom Aufrufer. */
export function autoStoerung(
  l: { letzteAntwortMs: number | null; letzterAusfallMs: number | null },
  jetzt: number,
  fenster: number = AUTO_STOERUNG_FENSTER_MS,
): boolean {
  if (l.letzterAusfallMs === null) return false;
  if (jetzt - l.letzterAusfallMs > fenster) return false;
  return l.letzteAntwortMs === null || l.letzteAntwortMs < l.letzterAusfallMs;
}

/** Ob eine Antwort des Servers ein brauchbarer Stand ist (App: Antwort prüfen). */
export function istAutoStand(v: unknown): v is AutoStand {
  if (typeof v !== "object" || v === null) return false;
  const s = v as Partial<AutoStand>;
  const zahl = (x: unknown): boolean => typeof x === "number" && Number.isFinite(x) && x >= 0;
  const heute = s.heute as Partial<AutoTagesZahlen> | undefined;
  return (
    typeof s.tag === "string" &&
    typeof s.stoerung === "boolean" &&
    heute !== undefined &&
    zahl(heute.senden) &&
    zahl(heute.warten) &&
    zahl(heute.rueckfall) &&
    (s.letzteAntwortMs === null || zahl(s.letzteAntwortMs)) &&
    (s.letzterAusfallMs === null || zahl(s.letzterAusfallMs))
  );
}

/** „14:32" — die Uhrzeit eines Zeitpunkts in Wiener Zeit. */
function wienerUhr(ms: number): string {
  return new Date(ms).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Vienna" });
}

/**
 * Der Satz unter der Wahl „Absenden nach Stille": wie weit Auto schon aktiv
 * ist. Ohne Auto steht dort, was Auto täte (eingeschaltet wird mit dem Knopf
 * „Auto" in der Reihe darüber — der Satz ist reiner Text); mit Auto die
 * Zahlen des Tages.
 *
 * ⚠️ Bei Störung/Rückfall steht die feste Frist dabei — sonst wüsste man
 *    nicht, wie lange es jetzt wirklich dauert.
 */
export function autoStandZeile(gewaehltMs: number, lage: AutoStandLage): string {
  if (gewaehltMs !== STILLE_AUTO_MS) return "Auto prüft, ob dein Satz fertig ist (Jev).";
  const frist = Math.round(AUTO_RUECKFALL_MS / 1000);
  if (lage === "laedt") return "Auto ist an.";
  if (lage === "fehlt") return "Auto ist an – der Stand von heute ist gerade nicht abrufbar.";
  if (lage.stoerung) return `Auto ist an – Jev antwortet gerade nicht, es gelten ${frist} Sekunden.`;
  const { senden, warten, rueckfall } = lage.heute;
  const teile: string[] = [];
  if (senden > 0) teile.push(`${senden}× sofort abgeschickt`);
  if (warten > 0) teile.push(`${warten}× länger gewartet`);
  if (rueckfall > 0) {
    const wann = lage.letzterAusfallMs !== null ? `zuletzt ${wienerUhr(lage.letzterAusfallMs)}, ` : "";
    teile.push(`${rueckfall}× ohne Jev (${wann}feste ${frist} s)`);
  }
  return teile.length ? `Auto ist an – heute ${teile.join(", ")}.` : "Auto ist an – heute noch nichts geprüft.";
}

/** Der Satz nach dem Umstellen der Wahl — in den Einstellungen und im Blatt gleich. */
export function stilleBestaetigung(ms: number, nachSatz = false): string {
  if (ms === STILLE_AUTO_MS) return "Auto: Nach einer kurzen Pause wird geprüft, ob dein Satz fertig ist.";
  if (!ms) return nachSatz ? "Nur der Knopf, ein Auslösewort oder dein Abschied schicken ab." : "Die Stille schickt nichts ab.";
  const s = Math.round(ms / 1000);
  // 20.10.2026: „Feste Stille“ des Reglers „Automatisch absenden“ (Konto 1) wartet auf einen fertigen Satz.
  return nachSatz ? `Nach ${s} Sekunden Stille geht es raus, wenn dein Satz fertig ist.` : `Nach ${s} Sekunden Stille geht es raus.`;
}
